// ============================================================================
// IMPORTS SECTION
// ============================================================================
// Routing
import { type To, useLocation, useNavigate } from "react-router-dom";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
// React hooks
import { useEffect } from "react";
import React, { useRef, useState } from "react";
// Components
import Instructions from "components/Instructions";
import PONumber from "components/PONumber";
import type { InvoiceType } from "components/InvoiceDocument";
import { OrderChargesView } from "components/Tax/OrderChargesView";
import { getActiveTaxType } from "components/Tax/manualtax";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
// Image handling
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
// Assets - Images
import back_png from "assets/images/brands/back.png";
import customerdetails from "assets/images/brands/customerdetails.png";
import ShippingDetails from "assets/images/brands/ShippingDetails.png";
import Pending from "assets/images/brands/Pending.png";
import delivered from "assets/images/brands/Delivered.png";
import Shipped from "assets/images/brands/Shipped.png";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { AppIcon } from "components/AppIcon";
import { DiscountAmountDisplay, getSignageOrderLinePricing } from "components/DiscountLabelBadge";
import { SalesOrderDetailsShipmentsTable } from "components/SalesOrderDetails/SalesOrderDetailsShipmentsTable";

// Icons
import { Pen, Pencil } from 'lucide-react';
// Styles
import "../assets/css/SignageOrders.css";
// HTTP client
import axios from "axios";
import { GetVersionOrdernumber } from "components/specialFeeTaxCloud";
// Error handling
import { handleApiError } from "utils/errorHandler";
import { showSuccess, showError } from "toast/toast";
import { getAuthCookie } from "../utils/authCookies";
import { sendTriggerEmails } from "utils/emailService";
import { resolveBrokerageTeamMemberEmail } from "utils/brokerageTeamMemberEmail";
import {
    buildInvoiceEmailParamsForSend,
    buildInvoiceSendRecipientPlan,
    sendInvoiceTriggerEmails,
} from "utils/invoiceEmailHelpers";
import SendOrderEmailRecipientsModal, {
    appendAdditionalRecipientEmails,
    mergeAdditionalIntoInvoiceCustomerRecipients,
} from "components/SendOrderEmailRecipientsModal";
import {
    buildSalesOrderInvoicePdfAttachment,
    buildSalesOrderPdfAttachment,
} from "utils/installationOrderPdfAttachment";
import {
    fetchPublicOrderQuoteModeSetting,
    fetchProjectApprovedMessage,
    resolveApprovedProofVersionSummary,
} from "components/proejctsandquoteapicalls";
import { resolveSignageIdFromOrder, displayPaymentPaidByName } from "components/orderSummary";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { downloadWorkDriveFile } from "api/realesyStudioApi";
import {
    formatShippingChargeOrderTotalsLabel,
} from "utils/orderTotalsChargeLabels";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";
import {
    computeSalesSubtotalBeforeDiscountFromItems,
    getSalesOrderLineChangedUnitPrice,
    getSalesOrderLineUnitRetailPrice,
} from "components/invoiceDocument/invoiceDocumentSalesTotals";
import type {
    AnyLineTax,
    LineTax,
    SalesOrderDetailsProps as Props,
} from "components/SalesOrderDetails/types";
// import { ACTIVE_TYPE_TENANT, logSendEvent } from "components/SalesOrderDetails/salesOrderDetailsSendLog";
import { logSendEvent } from "components/SalesOrderDetails/salesOrderDetailsSendLog";
import {
    coerceToLineTax,
    parseCustomInformation,
} from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";
import { buildYourOrderReadyEmailParams } from "components/SalesOrderDetails/buildYourOrderReadyEmailParams";
import { resolveSendQuoteEmailTrigger } from "components/SalesOrderDetails/resolveSendQuoteEmailTrigger";
import {
    SALES_ORDER_DETAILS_PAGE_HEADER_CSS,
    SALES_ORDER_DETAILS_PAYMENT_TXN_CSS,
} from "components/SalesOrderDetails/salesOrderDetailsEmbeddedCss";
import { SalesOrderDetailsPageHeader } from "components/SalesOrderDetails/SalesOrderDetailsPageHeader";
import { SalesOrderDetailsOrderLog } from "components/SalesOrderDetails/SalesOrderDetailsOrderLog";
import { buildSalesOrderOrderLogItems } from "components/SalesOrderDetails/buildSalesOrderOrderLogItems";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import RefreshIconButton from "components/RefreshIconButton";
import { batchPresignUrls } from "components/Imageshelper";
import {
    buildSalesResolvePayloadFromSavedOrder,
    getSalesOrderDiscountFieldDisplay,
    overlaySalesSubmitFromResolved,
    postSalesOrderResolve,
    syncSalesViewStateFromResolved,
    type ResolvedSalesOrder,
} from "orderResolve";

function renderLineItemCustomInformation(rowKey: string, item: any) {
    if (String(item?.inventory_source || "").toLowerCase() !== "custom") return null;

    const raw = String(item?.custom_information ?? "").trim();
    if (!raw) return null;

    const pairs = parseCustomInformation(raw);
    const rows = pairs.length > 0 ? pairs : [{ key: "Details", value: raw }];

    return (
        <div className="custom-fields-container mt-2">
            {rows.map(({ key, value }, idx) => (
                <div
                    key={`${rowKey}-ci-${key || idx}`}
                    className="mb-2"
                    style={{ maxWidth: 400, wordBreak: "break-word" }}
                >
                    <strong className="custom-cells order-card-log">{key || "Field"} :</strong>{" "}
                    <span className="order-card-log wrap-anywhere">{value}</span>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// MAIN COMPONENT: SharedOrderDetailsReturn
// ============================================================================
// This is a shared component for displaying order details across different user roles.
// It handles order information display, invoice generation, tax calculations,
// status updates, and order history timeline.
export default function SharedOrderDetailsReturn({
    login,
    order,
    onBack,
    onDownloadInvoice,
    onOrderStatus,
    timelineItems: _timelineItems,
    colors,
    parseDate,
    rightTopPanel,
    baseUrl = process.env.REACT_APP_BASE_URL || "",
    baseRoute,
    onRefreshOrder,

}: Props) {
    // ========================================================================
    // AUTHENTICATION
    // ========================================================================
    const token = getAuthCookie("sessionToken") || "";
    const location = useLocation();
    const isSignageTeammember =
        String(baseRoute ?? "").includes("/signageteammember") ||
        location.pathname.includes("/signageteammember");
    const [parentSignageId, setParentSignageId] = React.useState<string | null>(null);
    const runtimeUserIdForSignage =
        typeof window !== "undefined" ? (getAuthCookie("userId") ?? "") : "";
    const signageId =
        isSignageTeammember && parentSignageId ? parentSignageId : runtimeUserIdForSignage;

    React.useEffect(() => {
        if (!isSignageTeammember || !token) return;
        fetchParentHierarchy(token).then((res) => {
            if (res) setParentSignageId(res.parentSignageId);
        });
    }, [isSignageTeammember, token]);

    // ========================================================================
    // HELPER FUNCTIONS - Local to Component
    // ========================================================================
    // Convert value to number (safe, returns 0 for invalid values)
    const n = (v: any) => Number.isFinite(Number(v)) ? Number(v) : 0;
    // Round to 2 decimal places
    const r2 = (v: any) => Math.round(n(v) * 100) / 100;
    // Extract ID from various formats (_id, id, or direct value)
    const idOf = (v: any) => (v?._id || v?.id || v || "").toString();
    /** Linked project id on order (string or Mongo-shaped `{ $oid }`). */
    const projectLinkIdStr = (v: any) => {
        if (v == null || v === "") return "";
        if (typeof v === "object" && v.$oid != null) return String(v.$oid).trim();
        const s = (v?._id || v?.id || v || "").toString().trim();
        return s === "[object Object]" ? "" : s;
    };
    const linkedProjectId = projectLinkIdStr(
        (order as any)?.project_id ??
        (order as any)?.projectId ??
        (order as any)?.linked_project_id ??
        (order as any)?.linked_project?.id
    );
    const [approvedMessageData, setApprovedMessageData] = React.useState<any>(null);
    const [hasApprovedMessage, setHasApprovedMessage] = React.useState(false);

    const approvedProofCards = React.useMemo(() => {
        const approvedRoot = (approvedMessageData as any) || {};
        const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
        if (approvedFiles.length === 0) return [];

        const grouped = new Map<number, any[]>();
        approvedFiles.forEach((f: any) => {
            const version = Number(f?.version);
            const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(f);
        });

        return Array.from(grouped.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([version, files]) => {
                const first = files[0] || {};
                const compactProofFiles = files.map((f: any, idx: number) => ({
                    id: String(f?.id ?? f?._id ?? idx),
                    name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
                    href: String(f?.preview_url ?? "").trim() || String(f?.file_url ?? f?.url ?? "").trim(),
                    previewUrl: String(f?.preview_url ?? f?.file_url ?? f?.url ?? "").trim(),
                    fileId: String(f?.file_id ?? "").trim(),
                    quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
                    fileSize: Number(f?.file_size ?? 0),
                }));
                const approvedByName = String(
                    first?.design_approved_by?.name ??
                    first?.updated_by?.name ??
                    approvedRoot?.design_approved_by?.name ??
                    approvedRoot?.approved_by_name ??
                    (order as any)?.design_approved_by?.name ??
                    (order as any)?.project?.design_approved_by?.name ??
                    ""
                ).trim();
                const approvedAtRaw =
                    first?.design_approved_at ??
                    first?.updated_at ??
                    approvedRoot?.design_approved_at ??
                    approvedRoot?.approved_at ??
                    (order as any)?.design_approved_at ??
                    (order as any)?.project?.design_approved_at;
                const approvedAtLabel = approvedAtRaw ? parseDate(approvedAtRaw) : "";
                const shippingName = String(
                    first?.shipping_method?.name ??
                    approvedRoot?.shipping_method?.name ??
                    (order as any)?.shipping_method?.name ??
                    (order as any)?.shipping_method?.service_name ??
                    ""
                ).trim();
                const shippingDesc = String(
                    first?.shipping_method?.description ??
                    approvedRoot?.shipping_method?.description ??
                    (order as any)?.shipping_method?.description ??
                    ""
                ).trim();
                const shippingAmountRaw = first?.shipping_amount ?? approvedRoot?.shipping_amount;
                const shippingAmountLabel = Number.isFinite(Number(shippingAmountRaw))
                    ? `$${Number(shippingAmountRaw).toFixed(2)}`
                    : "";
                const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
                return {
                    version,
                    files: compactProofFiles,
                    approvedByName,
                    approvedAtLabel,
                    footerBy: String(approvedRoot?.assigned_to_name || "").trim(),
                    shippingName,
                    shippingDesc,
                    shippingAmountLabel,
                    orderSummary,
                };
            });
    }, [approvedMessageData, order]);

    React.useEffect(() => {
        let cancelled = false;
        if (!linkedProjectId || !token) {
            setHasApprovedMessage(false);
            setApprovedMessageData(null);
            return;
        }
        (async () => {
            try {
                const approved = await fetchProjectApprovedMessage(token, linkedProjectId);
                if (cancelled) return;
                const hasMessage = Boolean(approved?.has_approved_message);
                setHasApprovedMessage(hasMessage);
                setApprovedMessageData(hasMessage ? approved?.data ?? null : null);
            } catch {
                if (cancelled) return;
                setHasApprovedMessage(false);
                setApprovedMessageData(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [linkedProjectId, token]);
    const formatSkuDisplay = (sku: unknown) => {
        const raw = String(sku ?? "").trim();
        if (!raw) return "—";
        if (/^custom_sku(?:_|$)/i.test(raw)) return "Custom SKU";
        return raw.includes("_") ? raw.split("_").slice(0, -1).join("_") || raw : raw;
    };
    // Generate unique key for product row: "productId:variantId"
    const keyForRow = (it: any) => `${idOf(it?.product_id || it?.productId)}:${idOf(it?.variant_id || it?.variantId)}`;

    // ========================================================================
    // DISCOUNT ALLOCATION - Build default from loaded order
    // ========================================================================

    // helpers
    // Helpers (optional)
    const is24Hex = (s: string) => /^[a-f0-9]{24}$/i.test(s);
    const [notes, setNotes] = React.useState<string>(order?.notes ?? "");
    const [instructionFileUrlMap, setInstructionFileUrlMap] = useState<Record<string, string>>({});
    const [orderId, setOrderNumber] = useState<string | null>(null);
    const [orderSummarySending, setOrderSummarySending] = React.useState(false);
    const [quoteModeEnabled, setQuoteModeEnabled] = React.useState(false);
    const [customDesignPreviewSrc, setCustomDesignPreviewSrc] = React.useState<string | null>(null);
    // inside component
    const navigate = useNavigate();
    const safeNavigate = createSafeNavigate(navigate);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const files = Array.isArray((order as any)?.uploaded_files) ? (order as any).uploaded_files : [];
            const fileIds = files
                .map((f: any) => String(f?.file_id || "").trim())
                .filter((id: string) => /^[0-9a-f]{24}$/i.test(id));
            if (!token || fileIds.length === 0) {
                if (!cancelled) setInstructionFileUrlMap({});
                return;
            }
            try {
                const map = await batchPresignUrls(fileIds, token);
                if (!cancelled) setInstructionFileUrlMap(map || {});
            } catch {
                if (!cancelled) setInstructionFileUrlMap({});
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [(order as any)?.uploaded_files, token]);

    const instructionAttachments = React.useMemo(() => {
        const files = Array.isArray((order as any)?.uploaded_files) ? (order as any).uploaded_files : [];
        return files.map((f: any, index: number) => {
            const fileId = String(f?.file_id || "").trim();
            const href = (fileId && instructionFileUrlMap[fileId]) || String(f?.file_url || "").trim();
            return {
                id: fileId || `uploaded-${index}`,
                name: String(f?.file_name || "Attachment"),
                href,
                previewUrl: href,
                dateLabel: f?.uploaded_date ? new Date(f.uploaded_date).toLocaleDateString() : undefined,
            };
        });
    }, [(order as any)?.uploaded_files, instructionFileUrlMap]);

    // Order log from order GET: status_history.StatusName, payment_info, send_quote/invoice logs
    const mergedOrderLogItems = React.useMemo(
        () => buildSalesOrderOrderLogItems(order),
        [order]
    );
    // Keep local state in sync if order prop changes later
    React.useEffect(() => {
        setNotes(order?.notes ?? "");
    }, [order?.id, order?.notes]);
    React.useEffect(() => {
        if (login !== "signage") {
            setQuoteModeEnabled(false);
            return;
        }
        if (isSignageTeammember && !parentSignageId) {
            return;
        }
        const ownerId = String(signageId || "").trim();
        if (!ownerId) {
            setQuoteModeEnabled(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchPublicOrderQuoteModeSetting(ownerId);
                if (!cancelled) setQuoteModeEnabled(Boolean(res.enabled));
            } catch {
                if (!cancelled) setQuoteModeEnabled(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [login, signageId, isSignageTeammember, parentSignageId]);

    const handleSaveInstructionsOnly = async (
        newNotes: string,
        context?: { hasPendingFiles: boolean }
    ) => {
        if (!orderId) {
            showError("Missing order id");
            return;
        }

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/orders-update/${order?._id || order?.id}?update_context=tax`,
                {
                    version: order?.version,
                    notes: newNotes,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenStr}`, // you already compute tokenStr above
                    },
                }
            );

            if (response.status === 200) {
                showSuccess("Instructions updated successfully!");
                // Update local state
                setNotes(newNotes);
                // If files are pending, refresh after file upload finishes in Instructions component.
                if (!context?.hasPendingFiles) {
                    await onRefreshOrder?.();
                }
            } else {
                showError("Failed to update instructions");
            }
        } catch (error: any) {
            showError(`Error updating instructions: ${error?.message || "Unknown error"}`);
            // Send error email notification
            const orderId = order?._id || order?.id || "";
            await handleApiError(error, {
                module: "sales_order",
                action: "update_instructions",
                userId: tokenStr ? (getAuthCookie("userId") || undefined) : undefined,
                url: `${process.env.REACT_APP_API_URL}/orders-update/${order?._id || order?.id}?update_context=tax`,
                method: "PUT",
                additionalInfo: {
                    orderId: orderId,
                    orderNumber: order?.order_number
                }
            }, tokenStr);
        }
    };
    // ✅ a real string in all cases
    // 1) Token as a plain STRING
    const tokenStr: string = typeof window !== "undefined"
        ? (getAuthCookie("sessionToken") ?? "")
        : "";

    // 2) Headers OBJECT for getImageSrc
    const authHeaders: Record<string, string> = tokenStr
        ? { Authorization: `Bearer ${tokenStr}` }
        : {};

    // 3) Build variant→image map (hook expects a STRING as 2nd arg)
    const variantImgMap: Record<string, string> =
        useVariantImageUrlMap(order?.items ?? [], tokenStr) || {};
    const items: any[] = Array.isArray(order?.items) ? order.items : Array.isArray(order) ? order : [];

    // Compute the edit link target per login with dynamic baseRoute
    const editTo: To = baseRoute
        ? login === "signage"
            ? `${baseRoute}/editOrder/${order?.id}`
            : login === "realtor"
                ? { pathname: `${baseRoute}/order/checkout` }
                : { pathname: `${baseRoute}/order/checkout` }
        : login === "signage"
            ? `/signage/editOrder/${order?.id}`
            : login === "realtor"
                ? { pathname: `${baseRoute}/order/checkout` }
                : { pathname: `${baseRoute}/order/checkout` };

    const editState =
        login === "signage" ? undefined : { orderId: order?.id };

    const isSignage = String(login ?? "").toLowerCase() === "signage";
    const normalizedBaseRoute = String(baseRoute || "").toLowerCase();
    const isAgentRoute = normalizedBaseRoute.includes("agent");
    const isRealtorRoute = normalizedBaseRoute.includes("realtor");
    const isSignageRoute = isSignage || normalizedBaseRoute.includes("signage");
    const isAgentOrRealtorView = login === "agent" || login === "realtor";
    const normalizedPaymentStatus = String(order?.payment_status || "").trim().toLowerCase();
    const isOrderPaid = normalizedPaymentStatus == "paid";
    const normalizedTaxStatus = String(order?.order_tax_summary?.tax_status || "").trim().toLowerCase();
    // Agent/realtor: do not auto PATCH tax after payment — that update can wipe order_charges.
    const shouldAutoFinalizeTax = false;
    const canShowShipped =
        order?.status === 101 && (order?.payment_info?.length ?? 0) > 0;

    const hasBrokerage = Boolean(order?.brokerage_firm_id);
    const hasOffice = Boolean(order?.office && (order.office._id || order.office.officeName));
    const hasAgent = Boolean(order?.agent_id || order?.team_member_id);

    // Best-effort agent name
    const agentName =
        order?.agent_name ||
        // order?.team_member_name ||
        // [order?.customer_first_name, order?.customer_last_name].filter(Boolean).join(" ") ||
        "";
    const brokerageVal = hasBrokerage && (order?.brokerage_firm_name?.trim()?.length ?? 0) > 0
        ? order!.brokerage_firm_name!
        : "--";

    const officeVal = hasOffice && (order?.office?.officeName?.trim()?.length ?? 0) > 0
        ? order!.office!.officeName!
        : "--";

    const agentVal = hasAgent && (agentName.trim().length > 0) ? agentName : "--";

    // Decide "For" value (your rules)
    let forValue: string = "--";
    if (hasBrokerage && hasOffice) {
        forValue = "Office";
    } else if (hasBrokerage && hasAgent) {
        forValue = "Agent";
    } else if (hasAgent) {
        forValue = "Agent";
    } else {
        // fallback: customer name or --
        // const customer = [order?.customer_first_name, order?.customer_last_name].filter(Boolean).join(" ");
        forValue = "--";
    }

    const [sendInvoiceLoading, setSendInvoiceLoading] = useState(false);
    const [emailRecipientsModal, setEmailRecipientsModal] = useState<null | "quote" | "invoice">(null);
    const [activeTaxType, setActiveTaxType] = React.useState<"manual" | "taxcloud" | "geera" | "unknown">("unknown");
    const [shippingRows, setShippingRows] = React.useState<any[]>([]);
    const [taxByLineId, setTaxByLineId] =
        React.useState<Record<string, { amount: number; rate?: number }>>({});
    // store last upsert's line-by-id → used when "Report Sales Tax" finalizes
    const lastLineByItemIdRef = React.useRef<Record<string, { amount: number; rate?: number }>>({});
    const autoReportAttemptedRef = React.useRef(false);
    const deliverAfterTaxReportRef = React.useRef(false);
    const lastResolvedSalesRef = React.useRef<ResolvedSalesOrder | null>(null);
    const applySalesOrderResolveTaxRef = React.useRef<
        (action: "draft" | "complete") => Promise<boolean>
    >(async () => false);
    const [salesResolveTaxLoading, setSalesResolveTaxLoading] = React.useState(false);
    const [salesResolveTaxCommitted, setSalesResolveTaxCommitted] = React.useState(
        () => String(order?.order_tax_summary?.tax_status || "").trim().toLowerCase() === "complete"
    );

    React.useEffect(() => {
        if (!isSignage || !tokenStr || !signageId) return;
        let cancelled = false;
        (async () => {
            try {
                const t = await getActiveTaxType(tokenStr, signageId);
                const normalized = String(t || "").trim().toLowerCase();
                if (cancelled) return;
                if (normalized === "taxcloud" || normalized === "manual" || normalized === "geera") {
                    setActiveTaxType(normalized as "manual" | "taxcloud" | "geera");
                } else {
                    setActiveTaxType("unknown");
                }
            } catch {
                if (!cancelled) setActiveTaxType("unknown");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isSignage, tokenStr, signageId]);

    const handleMarkAsDelivered = React.useCallback(async () => {
        const shouldReport =
            login === "signage" &&
            order?.status !== 400 &&
            normalizedTaxStatus === "draft";

        if (shouldReport) {
            deliverAfterTaxReportRef.current = true;
            const reported = await applySalesOrderResolveTaxRef.current("complete");
            deliverAfterTaxReportRef.current = false;
            if (!reported) return;
        }

        onOrderStatus(302);
    }, [login, order?.status, normalizedTaxStatus, onOrderStatus]);

    const [specialFeeRows, setSpecialFeeRows] = React.useState<any[]>([]);
    // Read new nested charges and map to the *existing UI* row keys
    function pickChargesForUI(order: any) {
        const charges0 = Array.isArray(order?.order_charges) && order.order_charges.length > 0
            ? order.order_charges[0]
            : null;

        const shippingRaw = Array.isArray(charges0?.shipping_option)
            ? charges0.shipping_option
            : Array.isArray(order?.shipping_option)
                ? order.shipping_option
                : [];

        const feesRaw = Array.isArray(charges0?.specialfee)
            ? charges0.specialfee
            : Array.isArray(order?.specialfee)
                ? order.specialfee
                : [];

        const shippingUI = shippingRaw.map((r: any) => ({
            shipping__id: String(r.shipping__id ?? r.shipping_id ?? ""),
            shipping_name: String(r.shipping_name ?? ""),
            shipping_type: String(r.shipping_type ?? r.shippingType ?? ""),
            shipping_amount: Number(r.shipping_amount ?? 0),
            shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
            shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
            shipping_taxable: r.shipping_taxable !== false,
            shipping_delivarydate: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
            shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
            is_customer_pickup: r.is_customer_pickup === true,
        }));

        const feesUI = feesRaw.map((f: any) => ({
            specialfee_id: String(f.specialfee_id ?? ""),
            specialfee_name: String(f.specialfee_name ?? ""),
            specialfee_amount: Number(f.specialfee_amount ?? 0),
            specialfee_tax_percentage: Number(f.specialfee_tax_percentage ?? 0),
            specialfee_tax_amount: Number(f.specialfee_tax_amount ?? 0),
            specialfee_taxable: f.specialfee_taxable !== false,
            required: f.required === true,
            is_customer_pickup: f.is_customer_pickup === true,
        }));

        return { shippingUI, feesUI };
    }

    React.useEffect(() => {
        const { shippingUI, feesUI } = pickChargesForUI(order);
        setShippingRows(shippingUI);
        setSpecialFeeRows(feesUI);
    }, [order?.id, order?.order_charges, order?.shipping_option, order?.specialfee]);
    const isTrue = (v: any) =>
        v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");



    const getSavedRowDiscount = (it: any) => {
        const d1 = n(it?.discount_on_total_quantity);
        const d2 = n(it?.discount_on_total_quantity_amount);
        if (d1 > 0 || d2 > 0) return d1 || d2;

        const lineDisc = n(it?.discount ?? it?.discount_amount ?? 0);
        if (lineDisc > 0) return lineDisc;

        return 0;
    };

    const buildRowDiscountMapFromOrder = (items: any[]) => {
        const map: Record<string, number> = {};
        for (const it of items) {
            map[keyForRow(it)] = getSavedRowDiscount(it);
        }
        return map;
    };

    const [rowDiscountMap, setRowDiscountMap] = React.useState<Record<string, number>>(() =>
        buildRowDiscountMapFromOrder(Array.isArray(order?.items) ? order.items : [])
    );

    React.useEffect(() => {
        const items = Array.isArray(order?.items) ? order.items : [];
        setRowDiscountMap(buildRowDiscountMapFromOrder(items));
    }, [order?.items]);

    const getChangedUnit = (it: any, rowDisc?: number) => {
        const saved = n(it?.changed_unit_price ?? it?.changed_unitPrice);
        if (saved > 0) return saved;
        const qty = Math.max(1, n(it?.quantity));
        const unit = n(it?.changed_unit_price ?? it?.changed_unitPrice);
        const disc = rowDisc ?? getSavedRowDiscount(it);
        safeConsole.log(`  unit: ${unit}, qty: ${qty}, disc: ${disc}`);
        return r2(Math.max(0, unit * qty - disc) / qty);
    };

    const onPreviewInvoice = (t: InvoiceType) => {
        safeNavigate(`${baseRoute}/InvoiceDocument`, {
            state: {
                type: t,
                order, // pass the full order object used for the invoice
                imageUrlMap: variantImgMap, // optional thumbs
                // logoUrl, serviceInfo, resolveImageSrc, ... if needed
                autoPrint: false, // set true if you want it to auto-open the print dialog
            },
        });
    };

    const fromInvoiceEmail =
        process.env.REACT_APP_FROM_EMAIL2?.trim() || "noreply@realesy.com";

    const handleSendInvoiceEmail = async (additionalEmails: string[] = []) => {
        if (!order || sendInvoiceLoading) return;
        if (!tokenStr) {
            showError("Session expired. Please sign in again.");
            return;
        }
        const recipientPlan = await buildInvoiceSendRecipientPlan(order, false, tokenStr);
        recipientPlan.customerRecipients = mergeAdditionalIntoInvoiceCustomerRecipients(
            recipientPlan.customerRecipients,
            additionalEmails
        );
        const hasRecipients =
            recipientPlan.customerRecipients.emails.length > 0 ||
            recipientPlan.accountEmails.length > 0;
        if (!hasRecipients) {
            showError("No brokerage, customer, or account email found for this order; cannot send the invoice.");
            return;
        }
        setSendInvoiceLoading(true);
        try {
            const salesOrderId = String(order?._id || order?.id || "").trim();
            if (!salesOrderId) {
                throw new Error("Missing sales order id.");
            }
            const safeNum = String(
                order?.order_number ?? order?.job_number ?? order?.id ?? order?._id ?? "order"
            ).replace(/[^\w.-]+/g, "_");
            const pdfAttachment = await buildSalesOrderInvoicePdfAttachment({
                salesOrderId,
                token: tokenStr,
                fileLabel: safeNum,
                pdfFetch: {
                    maxAttempts: 8,
                    initialDelayMs: 400,
                    retryDelayMs: 600,
                    perFetchTimeoutMs: 20_000,
                },
            });
            if (!pdfAttachment) {
                throw new Error("Could not fetch invoice PDF from the server.");
            }
            const baseParams = await buildInvoiceEmailParamsForSend(order, tokenStr, "sales");
            await sendInvoiceTriggerEmails(
                baseParams,
                recipientPlan,
                fromInvoiceEmail,
                tokenStr,
                pdfAttachment
            );
            await logSendEvent(order, tokenStr, "invoice");
            showSuccess("Invoice email sent.");
        } catch (e) {
            showError(e instanceof Error ? e.message : "Failed to send invoice email.");
        } finally {
            setSendInvoiceLoading(false);
        }
    };

    /** Quote mode: send “Your order is ready” email; success toast only (no order summary preview). */
    const handleSendQuoteClick = async (additionalEmails: string[] = []) => {
        if (!order || orderSummarySending) return;
        const agentEmail = String(order?.customer_email || "").trim();
        if (!agentEmail) {
            showError("No customer email on this order; cannot send the order summary email.");
            return;
        }
        if (!tokenStr) {
            showError("Session expired. Please sign in again.");
            return;
        }
        setOrderSummarySending(true);
        try {
            await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
            const salesOrderId = String(order?._id || order?.id || "").trim();
            if (!salesOrderId) {
                showError("Missing sales order id. Email was not sent.");
                return;
            }
            const safeNum = String(order.order_number || order.id || order._id || "order").replace(
                /[^\w.-]+/g,
                "_"
            );
            const pdfAttachment = await buildSalesOrderPdfAttachment({
                salesOrderId,
                token: tokenStr,
                fileLabel: safeNum,
            });
            if (!pdfAttachment) {
                showError("Could not fetch order PDF. Email was not sent.");
                return;
            }
            const p = await buildYourOrderReadyEmailParams(order, tokenStr);
            const teamMemberEmail = resolveBrokerageTeamMemberEmail(order);
            const licenseTypes: string[] = ["AGENT"];
            const recipients: string[] = [agentEmail];
            if (
                teamMemberEmail &&
                teamMemberEmail.toLowerCase() !== agentEmail.toLowerCase()
            ) {
                licenseTypes.push("BROKERAGE_TEAM_MEMBER");
                recipients.push(teamMemberEmail);
            }
            const merged = appendAdditionalRecipientEmails(
                recipients,
                licenseTypes,
                additionalEmails
            );
            const sendQuoteTrigger = await resolveSendQuoteEmailTrigger("sales", order);
            await sendTriggerEmails(
                sendQuoteTrigger,
                merged.licenseTypes,
                {
                    customerName: p.customerName,
                    orderId: String(order.order_number || order.id || order._id || ""),
                    quantity: p.orderReadyQty,
                    totalDue: p.orderReadyTotal,
                    alreadyPaid: p.alreadyPaid,
                    remainingBalance: p.remainingBalance,
                    viewPayUrl: p.publicViewPayUrl,
                    lineItems: p.lineItems,
                    addressFieldRow: p.addressFieldRow,
                    email_signature_display_name: p.email_signature_display_name,
                },
                merged.emails,
                process.env.REACT_APP_FROM_EMAIL2 || "",
                tokenStr,
                pdfAttachment
            );
            await logSendEvent(order, tokenStr, "quote");
            showSuccess("Quote sent successfully.");
        } catch (e) {
            showError(e instanceof Error ? e.message : "Failed to send order summary email.");
        } finally {
            setOrderSummarySending(false);
        }
    };

    /** Non–quote mode: open order summary preview only (no email). */
    const handleOrderSummaryPreviewClick = () => {
        if (!order) return;
        const br = baseRoute || "/signage";
        try {
            const signageId = resolveSignageIdFromOrder(order);
            const orderForPreview = {
                ...order,
                signage_id: signageId || (order as any)?.signage_id || "",
            };
            try {
                sessionStorage.setItem(
                    "orderSummaryPrivatePreview",
                    JSON.stringify({
                        privateOrderSummaryPreview: true,
                        order: orderForPreview,
                        ts: Date.now(),
                    })
                );
            } catch {
                /* ignore quota / circular refs */
            }
            navigate(`${br}/OrderSummary`, {
                state: {
                    privateOrderSummaryPreview: true,
                    order: orderForPreview,
                },
            });
        } catch (e) {
            safeConsole.error("Order summary preview failed:", e);
            showError("Could not open order summary.");
        }
    };
    // ▼ show/hide breakdown
    const [showTaxDetails, setShowTaxDetails] = React.useState(false);

    const downloadCustomDesign = React.useCallback(
        async (previewUrlFileId: string, fileName = "custom-design") => {
            const id = String(previewUrlFileId ?? "").trim();
            if (!id) return;
            try {
                await downloadWorkDriveFile(id, fileName);
            } catch (e) {
                showError(e instanceof Error ? e.message : "Failed to download custom design.");
            }
        },
        [showError]
    );
    const [taxOverride, setTaxOverride] = React.useState<{ product: number; shipping: number; specialFee: number }>({
        product: 0, shipping: 0, specialFee: 0
    });

    // items array (already computed earlier as `items`), and nested charges reader:
    const charges0 = React.useMemo(
        () => (Array.isArray(order?.order_charges) ? order.order_charges[0] : null),
        [order?.order_charges]
    );
    const hasCustomerPickupShipping = React.useMemo(() => {
        const arr = (shippingRows && shippingRows.length)
            ? shippingRows
            : (Array.isArray(charges0?.shipping_option) ? charges0.shipping_option : []);
        return (arr as any[]).some((r: any) => r?.is_customer_pickup === true);
    }, [charges0?.shipping_option, shippingRows]);
    // sum of special-fee amounts (for Order Totals and grand total)
    const specialFeeAmountComputed = React.useMemo(() => {
        return (specialFeeRows || []).reduce((s, f) => s + Number(f?.specialfee_amount || 0), 0);
    }, [specialFeeRows]);

    const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

    // prefer resolve override when present; else use row-based sums
    const taxBreakdown = React.useMemo(() => {
        const itemsArr = Array.isArray(order?.items) ? order.items : [];

        // ✅ use persisted item_tax_info
        const productTaxRow = itemsArr.reduce(
            (s: number, it: any) => s + Number(it?.item_tax_info?.total_product_tax_amount || 0),
            0
        );

        // Prefer rows from state (already mapped), else fall back to order_charges directly
        const shipArr = (shippingRows && shippingRows.length)
            ? shippingRows
            : (Array.isArray(charges0?.shipping_option) ? charges0.shipping_option : []);
        const feeArr = (specialFeeRows && specialFeeRows.length)
            ? specialFeeRows
            : (Array.isArray(charges0?.specialfee) ? charges0.specialfee : []);

        const shippingTaxRow = shipArr.reduce(
            (s: number, r: any) => s + Number(r?.shipping_tax_amount || 0),
            0
        );
        const specialFeeTaxRow = feeArr.reduce(
            (s: number, f: any) => s + Number(f?.specialfee_tax_amount || 0),
            0
        );
        // Override bucket if resolve just returned a value > 0; otherwise use persisted rows
        const product = Number((taxOverride.product || 0) > 0 ? taxOverride.product : productTaxRow);
        const shipping = Number((taxOverride.shipping || 0) > 0 ? taxOverride.shipping : shippingTaxRow);
        const specialFee = Number((taxOverride.specialFee || 0) > 0 ? taxOverride.specialFee : specialFeeTaxRow);

        return {
            product: Math.round(product * 100) / 100,
            shipping: Math.round(shipping * 100) / 100,
            specialFee: Math.round(specialFee * 100) / 100,
        };
    }, [order?.items, shippingRows, specialFeeRows, charges0, taxOverride]);


    const taxTotalComputed = React.useMemo(() => {
        const sum = round2(taxBreakdown.product + taxBreakdown.shipping + taxBreakdown.specialFee);
        const fallback = Number(order?.tax_amount || 0);
        return sum > 0 ? sum : fallback;
    }, [taxBreakdown, order?.tax_amount]);



    // subtotal from items (prefer total_price; fallback to qty * unit_retail_price)
    const subtotalComputed = React.useMemo(() => {
        const arr = Array.isArray(order?.items) ? order.items : [];
        return arr.reduce((sum: number, it: any) => {
            const line = Number(
                it?.total_price ??
                (Number(it?.quantity || 0) * Number(it?.unit_retail_price || 0))
            );
            return sum + (Number.isFinite(line) ? line : 0);
        }, 0);
    }, [order?.items]);

    // shipping from nested charges (fallback to top-level order.shipping_amount)
    const shippingAmountComputed = React.useMemo(() => {
        const arr = Array.isArray(charges0?.shipping_option) ? charges0.shipping_option : [];
        const nestedSum = arr.reduce((s: number, r: any) => s + Number(r?.shipping_amount || 0), 0);
        const topLevel = Number(order?.shipping_amount || 0);
        return nestedSum > 0 ? nestedSum : topLevel;
    }, [charges0?.shipping_option, order?.shipping_amount]);
    const grandTotalComputed = React.useMemo(() => {
        // now includes special-fee charge
        const g = Number(subtotalComputed || 0)
            + Number(shippingAmountComputed || 0)
            + Number(specialFeeAmountComputed || 0)
            + Number(taxTotalComputed || 0);
        const fallback = Number(order?.total_amount || 0);
        return g > 0 ? g : fallback;
    }, [subtotalComputed, shippingAmountComputed, specialFeeAmountComputed, taxTotalComputed, order?.total_amount]);

    const shipRowsForOrderTotalsLabel = React.useMemo(() => {
        if (Array.isArray(shippingRows) && shippingRows.length > 0) return shippingRows;
        return Array.isArray(charges0?.shipping_option) ? charges0.shipping_option : [];
    }, [shippingRows, charges0?.shipping_option]);

    const feeRowsForOrderTotalsLabel = React.useMemo(() => {
        if (Array.isArray(specialFeeRows) && specialFeeRows.length > 0) return specialFeeRows;
        return Array.isArray(charges0?.specialfee) ? charges0.specialfee : [];
    }, [specialFeeRows, charges0?.specialfee]);

    const shippingChargeTotalsLabel = React.useMemo(() => {
        const rows = shipRowsForOrderTotalsLabel;
        const r0 = rows[0];
        const rawId = String((r0 as any)?.shipping__id ?? (r0 as any)?.shipping_id ?? "");
        const isUps =
            /^ups$/i.test(String((r0 as any)?.shipping_type ?? "").trim()) ||
            (/^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual");
        return formatShippingChargeOrderTotalsLabel(rows, { isUps });
    }, [shipRowsForOrderTotalsLabel]);

    const specialFeeBreakdownDisplay = React.useMemo(
        () => buildSpecialFeeCostBreakdownDisplay(feeRowsForOrderTotalsLabel),
        [feeRowsForOrderTotalsLabel]
    );

    // map UI shipping row -> API row
    const uiShippingToApi = (r: any) => ({
        shipping_id: String(r.shipping__id ?? r.shipping_id ?? ""),
        shipping_name: String(r.shipping_name ?? ""),
        shipping_type:
            String(r.shipping_type ?? "").trim() ||
            (String(r.shipping__id ?? r.shipping_id ?? "") === "custom-manual" ? "MANUAL" : ""),
        shipping_amount: Number(r.shipping_amount ?? 0),
        shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
        shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
        shipping_taxable: r?.shipping_taxable !== false,
        shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
        shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
        is_customer_pickup: r?.is_customer_pickup === true,
    });

    async function persistOrderCharges(
        orderId: string,
        token: string,
        shippingRows: any[],
        specialFeeRows: any[],
        taxStatus: "draft" | "complete" = "draft"
    ) {
        if (isAgentOrRealtorView) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/orders-update/${orderId}?update_context=tax`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    order_tax_summary: { tax_status: taxStatus },
                    order_charges: [{ shipping_option: shippingRows, specialfee: specialFeeRows }],
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || errorData?.message || `HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            // Send error email notification
            await handleApiError(error, {
                module: "sales_order",
                action: "persist_order_charges",
                userId: token ? (getAuthCookie("userId") || undefined) : undefined,
                url: `${process.env.REACT_APP_API_URL}/orders-update/${orderId}?update_context=tax`,
                method: "PATCH",
                additionalInfo: {
                    orderId: orderId,
                    orderNumber: order?.order_number,
                    taxStatus: taxStatus
                }
            }, token);
            throw error; // Re-throw to allow caller to handle
        }
    }


    // changed unit (after discount) with fallback


    // pre-tax, post-discount line subtotal
    const getItemSub = (it: any, rowDisc?: number) => {
        const qty = Math.max(1, n(it?.quantity));
        return r2(getChangedUnit(it, rowDisc) * qty);
    };

    // tax from live overrides first, fallback to saved
    const getLineTax = (it: any, taxByLineId: Record<string, { amount: number; rate?: number }>) => {
        const skuKey = String(it?.sku || it?.itemId || it?.variant_id || it?.product_id || "");
        const live = taxByLineId?.[skuKey]?.amount;
        if (live != null) return r2(live);
        return r2(n(it?.item_tax_info?.total_product_tax_amount));
    };

    // tax-inclusive line total
    const getLineTotal = (it: any, rowDisc: number, taxByLineId: Record<string, { amount: number }>) =>
        r2(getItemSub(it, rowDisc) + getLineTax(it, taxByLineId));

    /** Product table pricing — matches Signage create/edit (volume discount per line, coupon in order totals). */
    const getProductLinePricing = (it: any) => {
        const qty = Math.max(1, n(it?.quantity));
        const actualUnitPrice = getSalesOrderLineUnitRetailPrice(it);
        const changedUnitPrice = getSalesOrderLineChangedUnitPrice(it);
        const rowTaxAmt = getLineTax(it, taxByLineId);
        const { rowDiscount, lineTotal } = getSignageOrderLinePricing({
            actualUnitPrice,
            changedUnitPrice,
            qty,
            tax: rowTaxAmt,
        });
        return {
            qty,
            unitRetail: actualUnitPrice,
            changedUnit: changedUnitPrice,
            lineDiscount: rowDiscount,
            rowTaxAmt,
            lineTotal,
        };
    };

    const subTotalToShow = React.useMemo(
        () => computeSalesSubtotalBeforeDiscountFromItems(Array.isArray(order?.items) ? order.items : []),
        [order?.items]
    );

    const salesDiscountDisplay = React.useMemo(
        () =>
            getSalesOrderDiscountFieldDisplay({
                savedCode: String(order?.discount_code || order?.promo_code || ""),
                savedAmount: Number(order?.discount_amount || 0),
            }),
        [order?.discount_code, order?.promo_code, order?.discount_amount]
    );
    const discountToShow = salesDiscountDisplay.amount;
    const discountCodeToShow = salesDiscountDisplay.code;

    // Total tax for all product items
    const itemsTaxTotal = React.useMemo(() => {
        const items = Array.isArray(order?.items) ? order.items : [];
        return r2(items.reduce((s: any, it: any) => s + getLineTax(it, taxByLineId), 0));
    }, [order?.items, taxByLineId]);

    // Shipping and special fee amounts and taxes
    const shippingAmount = r2((shippingRows || []).reduce((t: number, r: any) => t + n(r.shipping_amount), 0));
    const shippingTax = r2((shippingRows || []).reduce((t: number, r: any) => t + n(r.shipping_tax_amount), 0));
    const specialAmount = r2((specialFeeRows || []).reduce((t: number, f: any) => t + n(f.specialfee_amount), 0));
    const specialTax = r2((specialFeeRows || []).reduce((t: number, f: any) => t + n(f.specialfee_tax_amount), 0));

    // Combined totals
    const totalTaxAll = r2(itemsTaxTotal + shippingTax + specialTax);

    const grandTotalPanel = React.useMemo(
        () =>
            r2(
                subTotalToShow +
                    n(shippingAmountComputed) +
                    n(specialFeeAmountComputed) +
                    n(taxTotalComputed) -
                    n(discountToShow)
            ),
        [subTotalToShow, shippingAmountComputed, specialFeeAmountComputed, taxTotalComputed, discountToShow]
    );

    const totalPaid = React.useMemo(
        () =>
            (order?.payment_info ?? []).reduce(
                (acc: number, p: { amount_paid?: number | string }) =>
                    acc + Number(p?.amount_paid || 0),
                0
            ),
        [order?.payment_info]
    );
    const remainingBalance = Number(grandTotalPanel) - totalPaid;

    type LineTaxLoose = { amount?: number; rate?: number };
    type LineTaxStrict = { amount: number; rate?: number };

    const normalizeLineMap = (
        m: Record<string, LineTaxLoose> | undefined | null
    ): Record<string, LineTaxStrict> => {
        const out: Record<string, LineTaxStrict> = {};
        if (!m) return out;
        for (const [k, v] of Object.entries(m)) {
            out[k] = { amount: Number(v?.amount ?? 0), rate: v?.rate };
        }
        return out;
    };


    // money + safe number helpers (reuse if you already have similar)
    const asNum = (v: any) => Number(v || 0);

    // ---------- helpers ----------
    const toId = (v: any): string =>
        typeof v === "string" ? v : (v?.$oid || v?.oid || v?.id || "");

    const num = (v: any): number =>
        v == null || v === "" || Number.isNaN(+v) ? 0 : +v;

    // Try a few keys to find the correct line tax (variant_id is preferred)
    const getLineTaxFor = (
        item: any,
        lineByItemId: Record<string, AnyLineTax>
    ): LineTax | null => {
        const keysToTry = [
            toId(item?.variant_id),   // preferred
            toId(item?._id),
            toId(item?.product_id),
            item?.sku,
        ].filter(Boolean) as string[];

        const appliedState =
            String(order?.shipping_address?.address_details?.stateCode ||
                order?.shipping_address?.address_details?.state || "");

        const qty = num(item?.quantity);
        const unit = num(item?.unit_retail_price ?? item?.unit_price ?? item?.unit_purchase_price);

        for (const k of keysToTry) {
            const raw = lineByItemId[k];
            if (raw) return coerceToLineTax(raw, qty, unit, appliedState);
        }
        return null;
    };

    // ---------- main payload builder ----------
    function buildOrderPatchPayload({
        order,
        taxStatus,
        lineByItemId,
        shippingRows,
        specialFeeRows,
        rowDiscountMap,
    }: {
        order: any;
        taxStatus: "draft" | "complete";
        lineByItemId: Record<string, { amount: number; rate?: number }>;
        shippingRows: any[];
        specialFeeRows: any[];
        rowDiscountMap: Record<string, number>;
    }) {
        const items = (order?.items || []).map((it: any) => {
            const k = keyForRow(it);
            const qty = Math.max(1, n(it?.quantity));
            const unit = n(it?.unit_retail_price ?? it?.unit_price ?? it?.unit_purchase_price);
            const rowDisc = n(rowDiscountMap[k] ?? getSavedRowDiscount(it));
            const perUnitDisc = r2(rowDisc / qty);
            const changedUnit = getChangedUnit(it, rowDisc);      // after discount
                        const discountAmount = Number(it?.discount_on_total_quantity || 0);
           const subtotalAmount = r2(r2(changedUnit * qty) + r2(discountAmount));
            const itemSub = r2(changedUnit * qty);                // pre-tax
            const taxAmt = getLineTax(it, lineByItemId);          // from live map (or saved)
            const total = r2(itemSub + taxAmt);                   // tax-inclusive

            const by = lineByItemId?.[String(it?.sku || it?.itemId || it?.variant_id || it?.product_id || "")];
            const ratePct = n((by?.rate ?? 0) * 100);

            // Do not derive taxable / is_taxable from tax amounts — only pass through existing order data.
            const preservedIsTaxable =
                typeof it?.is_taxable === "boolean"
                    ? it.is_taxable
                    : typeof it?.product?.is_taxable === "boolean"
                        ? it.product.is_taxable
                        : typeof it?.variant?.is_taxable === "boolean"
                            ? it.variant.is_taxable
                            : undefined;

            const preservedItemTaxInfoTaxable =
                typeof it?.item_tax_info?.taxable === "boolean"
                    ? it.item_tax_info.taxable
                    : preservedIsTaxable;

            return {
                product_id: idOf(it?.product_id),
                variant_id: idOf(it?.variant_id),
                sku: String(it?.sku || ""),
                product_name: it?.product_name ?? it?.name ?? "",
                quantity: qty,

                unit_retail_price: r2(unit),
                changed_unit_price: r2(changedUnit),

                discount_on_unit_quantity: r2(perUnitDisc),
                discount_on_total_quantity: r2(rowDisc),

                item_sub_total: r2(itemSub),
                total_price: r2(total),

                ...(preservedIsTaxable !== undefined ? { is_taxable: preservedIsTaxable } : {}),

                item_tax_info: {
                    tax_percentage: r2(ratePct || it?.item_tax_info?.tax_percentage || 0),
                    unit_product_tax_amount: r2(qty > 0 ? taxAmt / qty : 0),
                    total_product_tax_amount: r2(taxAmt),
                    ...(preservedItemTaxInfoTaxable !== undefined
                        ? { taxable: preservedItemTaxInfoTaxable }
                        : {}),
                    tax_details: {
                        tax_rate: r2(ratePct || it?.item_tax_info?.tax_details?.tax_rate || 0),
                        tax_amount: r2(taxAmt),
                        applied_state: it?.item_tax_info?.tax_details?.applied_state ?? (order?.shipping_address?.address_details?.stateCode || ""),
                    },
                },
            };
        });

        // order level sums
        const itemsSubtotal = r2(items.reduce((s: any, it: any) => s + n(it.item_sub_total), 0));
        console.log("itemsSubtotal", itemsSubtotal);
        const itemsTax = r2(items.reduce((s: any, it: any) => s + n(it.item_tax_info?.total_product_tax_amount), 0));
        const shipAmt = r2((shippingRows || []).reduce((s, r: any) => s + n(r.shipping_amount), 0));
        const shipTax = r2((shippingRows || []).reduce((s, r: any) => s + n(r.shipping_tax_amount), 0));
        const feeAmt = r2((specialFeeRows || []).reduce((s, f: any) => s + n(f.specialfee_amount), 0));
        const feeTax = r2((specialFeeRows || []).reduce((s, f: any) => s + n(f.specialfee_tax_amount), 0));

        const orderItems = Array.isArray(order?.items) ? order.items : [];
        const lineDiscountSum = r2(
            orderItems.reduce((s: number, it: any) => {
                const k = keyForRow(it);
                return s + n(rowDiscountMap[k] ?? getSavedRowDiscount(it));
            }, 0)
        );
        const discount_amount = r2(
            lineDiscountSum > 0 ? lineDiscountSum : n(order?.discount_amount ?? order?.discount ?? 0)
        );
        console.log("discount_amount", discount_amount, { lineDiscountSum, rowDiscountMap });
        const discount_code = String(order?.discount_code || order?.promo_code || "").trim();
        const promo_code = discount_code;

        const totalTax = r2(itemsTax + shipTax + feeTax);
        const subtotal_amounts = itemsSubtotal;                       // post-discount, pre-tax
        const sub_total_amount = r2(itemsSubtotal);                       
        const total_amount = r2(subtotal_amounts + shipAmt + feeAmt + totalTax-discount_amount);
        console.log("total_amount", total_amount);
        const overallPreTax = r2(subtotal_amounts + shipAmt + feeAmt);
        const overallPct = overallPreTax > 0 ? (totalTax / overallPreTax) * 100 : 0;

        return {
            // minimal patch fields you already send + these corrected numbers:
            items,
            subtotal_amount:sub_total_amount,
            shipping_amount: shipAmt,
            tax_amount: totalTax,
            total_amount,
            // discount_amount,
            // discount_code,
            promo_code,
            order_charges: [{
                shipping_option: (shippingRows || []).map((r: any) => ({
                    shipping_id: String(r.shipping__id ?? r.shipping_id ?? ""),
                    shipping_name: String(r.shipping_name ?? ""),
                    shipping_amount: n(r.shipping_amount),
                    shipping_tax_percentage: n(r.shipping_tax_percentage),
                    shipping_tax_amount: n(r.shipping_tax_amount),
                    shipping_taxable: r?.shipping_taxable !== false,
                    shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
                    shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
                    is_customer_pickup: isTrue(r?.is_customer_pickup),
                })),
                specialfee: (specialFeeRows || []).map((f: any) => ({
                    specialfee_id: String(f.specialfee_id ?? ""),
                    specialfee_name: String(f.specialfee_name ?? ""),
                    specialfee_amount: n(f.specialfee_amount),
                    specialfee_tax_percentage: n(f.specialfee_tax_percentage),
                    specialfee_tax_amount: n(f.specialfee_tax_amount),
                    specialfee_taxable: f?.specialfee_taxable !== false,
                    required: f?.required === true,
                })),
            }],
            order_tax_summary: {
                tax_inclusive: false,
                over_all_tax_amount: totalTax,
                over_all_tax_percentage: Number(overallPct.toFixed(4)),
                over_all_taxable_base: r2(overallPreTax),
                tax_status: taxStatus,
            },
        };
    }

    const applySalesOrderResolveTax = React.useCallback(
        async (action: "draft" | "complete"): Promise<boolean> => {
            if (!tokenStr || !order) return false;
            if (isAgentOrRealtorView) return false;

            setSalesResolveTaxLoading(true);
            try {
                const payload = buildSalesResolvePayloadFromSavedOrder(
                    order as Record<string, unknown>,
                    { shippingRows, feeRows: specialFeeRows }
                );
                const resolved = await postSalesOrderResolve(tokenStr, {
                    order_type: "order",
                    action,
                    payload,
                });
                lastResolvedSalesRef.current = resolved;

                const synced = syncSalesViewStateFromResolved(
                    resolved,
                    shippingRows,
                    specialFeeRows
                );
                const normalized = normalizeLineMap(synced.taxByLineId);
                setTaxByLineId(normalized);
                lastLineByItemIdRef.current = normalized;
                setShippingRows(synced.shippingRows);
                setSpecialFeeRows(synced.specialFeeRows);

                const taxStatus = action === "complete" ? "complete" : "draft";
                const bodyPatch = overlaySalesSubmitFromResolved(
                    buildOrderPatchPayload({
                        order,
                        taxStatus,
                        lineByItemId: normalized,
                        shippingRows: synced.shippingRows,
                        specialFeeRows: synced.specialFeeRows,
                        rowDiscountMap,
                    }),
                    resolved
                );

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/orders-update/${order?.id || order?._id}?update_context=tax`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${tokenStr}`,
                        },
                        body: JSON.stringify(bodyPatch),
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: response.statusText }));
                    throw new Error(
                        errorData.message || `Failed to update order tax (${action}): ${response.statusText}`
                    );
                }

                if (action === "complete") {
                    setSalesResolveTaxCommitted(true);
                }

                await onRefreshOrder?.();

                if (action === "complete" && !deliverAfterTaxReportRef.current) {
                    safeNavigate(0);
                }

                return true;
            } catch (error) {
                const orderIdForErr = order?._id || order?.id || "";
                await handleApiError(
                    error,
                    {
                        module: "sales_order",
                        action:
                            action === "complete"
                                ? "sales_resolve_report_tax_complete"
                                : "sales_resolve_apply_taxes_draft",
                        userId: tokenStr ? (getAuthCookie("userId") || undefined) : undefined,
                        url: `${process.env.REACT_APP_API_URL}/orders-update/${orderIdForErr}?update_context=tax`,
                        method: "PATCH",
                        additionalInfo: {
                            orderId: orderIdForErr,
                            orderNumber: order?.order_number,
                            taxStatus: action,
                        },
                    },
                    tokenStr
                );
                showError(
                    `Failed to ${action === "complete" ? "complete tax reporting" : "get live taxes"}: ${
                        error instanceof Error ? error.message : "Unknown error"
                    }`
                );
                return false;
            } finally {
                setSalesResolveTaxLoading(false);
            }
        },
        [
            order,
            tokenStr,
            shippingRows,
            specialFeeRows,
            rowDiscountMap,
            onRefreshOrder,
            safeNavigate,
            isAgentOrRealtorView,
        ]
    );

    applySalesOrderResolveTaxRef.current = applySalesOrderResolveTax;

    React.useEffect(() => {
        if (normalizedTaxStatus === "complete") {
            setSalesResolveTaxCommitted(true);
        }
    }, [normalizedTaxStatus]);

    React.useEffect(() => {
        if (!order) return;
        if (!shouldAutoFinalizeTax) return;
        if (!tokenStr) return;
        if (autoReportAttemptedRef.current) return;

        let cancelled = false;
        autoReportAttemptedRef.current = true;

        (async () => {
            try {
                const ok = await applySalesOrderResolveTax("complete");
                if (cancelled || !ok) return;
            } catch (error) {
                safeConsole.error("[SalesOrderDetails] Failed to auto finalize sales resolve tax", error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [order, shouldAutoFinalizeTax, tokenStr, applySalesOrderResolveTax]);


    // collect file ids from customizations
    const customizationIds = React.useMemo(() => {
        const out: string[] = [];
        const items = Array.isArray(order?.items) ? order!.items : [];
        for (const it of items) {
            if (it?.design_id && typeof it?.preview_url === "string" && it.preview_url.trim()) {
                out.push(it.preview_url.trim());
            }
            const fields = Array.isArray(it?.customizations?.fields) ? it.customizations.fields : [];
            for (const f of fields) {
                if (String(f?.field_type || "").toLowerCase() !== "file") continue;
                const raw = f?.value;
                const id = typeof raw === "string" ? raw.trim() : (raw?.url ? String(raw.url).trim() : "");
                if (/^[0-9a-f]{24}$/i.test(id)) out.push(id);
            }
        }
        return Array.from(new Set(out)); // de-dupe
    }, [order?.items]);

    // create a synthetic row that the hook will parse via `row.images[].url`
    const syntheticRowsForCustomizations = React.useMemo(() => {
        if (!customizationIds.length) return [];
        return [{ images: customizationIds.map((id) => ({ url: id })) }];
    }, [customizationIds]);

    // payload for the map hook = real items + synthetic customization rows
    const mapPayload = React.useMemo(() => {
        const items = Array.isArray(order?.items) ? order!.items : [];
        return [...items, ...syntheticRowsForCustomizations];
    }, [order?.items, syntheticRowsForCustomizations]);

    // build the URL map (presigned urls for ids, legacy for uploads/*)
    const variantImgMap1 = useVariantImageUrlMap(mapPayload, tokenStr);
    // money/number helpers already exist in file; reuse them if present.


    // prefer explicit payload fields, else infer from prices
    const getRowDiscount = (it: any): number => {
        // explicit fields the backend may send for saved orders
        const d1 = n(it?.discount_on_total_quantity);
        const d2 = n(it?.discount_on_total_quantity_amount);
        if (d1 > 0 || d2 > 0) return d1 || d2;

        // infer from unit_retail_price vs changed_unit_price
        const qty = n(it?.quantity);
        const unit = n(it?.unit_price ?? it?.unit_purchase_price);
        const changed = n(it?.changed_unit_price);
        if (qty > 0 && changed > 0 && unit >= changed) {
            return r2(qty * (unit - changed));
        }
        return 0;
    };

    // item_sub_total (after discount, before tax)
    const getItemSubTotal = (it: any): number => {
        // console.log(it, "itesm");
        const explicit = n(it?.total_price);
        //   if (explicit > 0) return explicit;

        const qty = n(it?.quantity);
        const changed = n(it?.total_price);

        const changedprice = (qty * changed);
        //   if (qty > 0 && changed > 0) return r2(qty * changed);

        // console.log(it?.total_price, "itesm122");
        // final fallback to total_price if nothing else is present
        return n(it?.total_price);
    };

    const getItemSubTotalprice = (it: any): number => {
        ;

        const qty = n(it?.quantity);
        const changed = n(it?.changed_unit_price || it?.changed_unitPrice);

        const changedprice = (qty * changed);

        return n(changedprice);
    };

    useEffect(() => {
        (async () => {
            if (!token || !order) return;

            try {
                const orderdata = (await GetVersionOrdernumber(
                    token,
                    order.order_number,
                    order?.order_type || "sales"
                )) || null;
                const orderId = (orderdata as any)?.full_number || (orderdata as any)?.order_number || "";

                setOrderNumber(orderId);
            } catch (error) {
                safeConsole.error("Error fetching order version number:", error);
                // Send error email notification
                await handleApiError(error, {
                    module: "sales_order",
                    action: "get_version_order_number",
                    userId: tokenStr ? (getAuthCookie("userId") || undefined) : undefined,
                    url: `${process.env.REACT_APP_API_URL}/utils/get-version-order-number`,
                    method: "GET",
                    additionalInfo: {
                        orderId: order?._id || order?.id || "",
                        orderNumber: order?.order_number
                    }
                }, tokenStr);
            }
        })();
    }, [token, order]);
    // id + number helpers


    // derive the row discount from saved fields
    /** EasyPost labels datatable filters — `order_id` = version full number, `order_number` = short number. */
    const orderNumberForShipments = React.useMemo(
        () => String(order?.order_number ?? order?.orderNumber ?? "").trim(),
        [order?.order_number, order?.orderNumber]
    );
    const orderIdForShipments = React.useMemo(
        () => String(order?._id ??order?.id ?? "").trim(),
        [order?._id, order?.id]
    );

    const gatedRightTopPanel = rightTopPanel;


    return (
        <div className="teammember container " >

            <SalesOrderDetailsPageHeader
                onBack={onBack}
                order={order}
                login={String(login ?? "")}
                baseRoute={baseRoute}
                quoteModeEnabled={quoteModeEnabled}
                editTo={editTo}
                editState={editState}
                orderSummarySending={orderSummarySending}
                onSendQuoteClick={() => setEmailRecipientsModal("quote")}
                onOrderSummaryPreviewClick={handleOrderSummaryPreviewClick}
                onPreviewInvoice={onPreviewInvoice}
                sendInvoiceLoading={sendInvoiceLoading}
                onSendInvoiceEmail={() => setEmailRecipientsModal("invoice")}
                onOrderStatus={onOrderStatus}
                isSignage={isSignage}
            />
            <style>{SALES_ORDER_DETAILS_PAGE_HEADER_CSS}</style>
            <SendOrderEmailRecipientsModal
                show={emailRecipientsModal != null}
                title={emailRecipientsModal === "invoice" ? "Send Invoice" : "Send Quote"}
                token={tokenStr}
                primaryEmailHint={String(order?.customer_email || "").trim() || undefined}
                confirming={
                    emailRecipientsModal === "invoice" ? sendInvoiceLoading : orderSummarySending
                }
                onCancel={() => setEmailRecipientsModal(null)}
                onConfirm={async (additionalEmails) => {
                    const mode = emailRecipientsModal;
                    try {
                        if (mode === "invoice") {
                            await handleSendInvoiceEmail(additionalEmails);
                        } else if (mode === "quote") {
                            await handleSendQuoteClick(additionalEmails);
                        }
                    } finally {
                        setEmailRecipientsModal(null);
                    }
                }}
            />


            <div className="row">
                {/* Left side: Main content */}
                <div className="col-lg-8 " id="left-column">
                    {/* CUSTOMER DETAILS */}
                    <div className="card box-shadow-datatables shadow-border-2 mt-4 ">
                        <div
                            className="px-3 py-3 rounded-top d-flex align-items-center justify-content-between gap-3"
                            style={{ backgroundColor: "#eff6ff" }}
                        >
                            <h6 className="mb-0 d-flex align-items-center mt-2 order-tittle" style={{ gap: 8 }}>
                                <AppIcon name="customerDetails" size={22} alt="Customer Details" />
                                <span>Customer Details</span>
                            </h6>
                            <RefreshIconButton
                                ariaLabel="Refresh customer details"
                                title="Refresh customer details"
                                onClick={async () => {
                                    const documentId = String(order?.id || order?._id || "").trim();
                                    const customerId = String(order?.customer_id || "").trim();
                                    if (tokenStr && documentId && customerId) {
                                        try {
                                            await fetch(`${process.env.REACT_APP_API_URL}/sync-user-email-to-document`, {
                                                method: "POST",
                                                headers: {
                                                    Authorization: `Bearer ${tokenStr}`,
                                                    "Content-Type": "application/json",
                                                },
                                                body: JSON.stringify({
                                                    user_id: customerId,
                                                    id: documentId,
                                                    collection_name: "sales",
                                                }),
                                            });
                                        } catch (syncErr) {
                                            safeConsole.warn("sync-user-email-to-document failed:", syncErr);
                                        }
                                    }
                                    await onRefreshOrder?.();
                                }}
                            />
                        </div>

                        <div className="card-body">
                            <div className="row gy-3">
                                <div className="col-12 col-lg-6">
                                    <div className="text-sm order-card-title mb-1">For:</div>
                                    <div className="order-card-cells">{forValue}</div>
                                </div>

                                <div className="col-12 col-lg-6">
                                    <div className="text-sm order-card-title mb-1">Brokerage :</div>
                                    <div className="order-card-cells">{brokerageVal}</div>
                                </div>

                                <div className="col-12 col-lg-6">
                                    <div className="text-sm order-card-title mb-1">Office :</div>
                                    <div className="order-card-cells">{officeVal}</div>
                                </div>

                                <div className="col-12 col-lg-6">
                                    <div className="text-sm order-card-title mb-1">Agent :</div>
                                    <div className="order-card-cells">{agentVal}</div>
                                </div>

                                <div className="col-12 col-lg-4">
                                    <div className="text-sm order-card-title mb-1">First Name:</div>
                                    <div className="order-card-cells">{order?.customer_first_name || "-"}</div>
                                </div>

                                <div className="col-12 col-lg-4">
                                    <div className="text-sm order-card-title mb-1">Last Name:</div>
                                    <div className="order-card-cells">{order?.customer_last_name || "-"}</div>
                                </div>

                                <div className="col-12 col-lg-4">
                                    <div className="text-sm order-card-title mb-1">Email:</div>
                                    <div className="order-card-cells">{order?.customer_email || "-"}</div>
                                </div>

                                <div className="col-12 col-lg-4">
                                    <div className="text-sm order-card-title mb-1">Phone:</div>
                                    <div className="order-card-cells">{order?.customer_phone || "-"}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BILLING + SHIPPING (exact like screenshot #2) */}
                    <div className="row g-4 ms-0" id="row-overlap">
                        {/* Billing Address */}
                        <div className="col-12 col-lg-6" id="row-overlap">
                            <div className="card box-shadow-datatables shadow-border-2">
                                <div className="px-3 py-3 rounded-top" style={{ backgroundColor: "#eff6ff" }}>
                                    <h6 className="mb-0 d-flex align-items-center order-tittle">
                                        <AppIcon name="billingAddress" size={22} alt="Billing Address" />
                                        <p className="mb-0 ms-2">Billing Address</p>
                                    </h6>
                                </div>

                                <div className="card-body">
                                    <div className="row gy-3">
                                        <div className="col-12 col-lg-6">
                                            <div className="addr-label order-card-title">First Name:</div>
                                            <div className="addr-value order-card-cells">{order?.customer_first_name || "-"}</div>
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <div className="addr-label order-card-title">Last Name:</div>
                                            <div className="addr-value order-card-cells">{order?.customer_last_name || "-"}</div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-label order-card-title">Phone Number:</div>
                                            <div className="addr-value order-card-cells">{order?.billing_address?.address_details?.phone || "-"}</div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-label order-card-title">Address Line 1:</div>
                                            <div className="addr-value order-card-cells">
                                                {order?.billing_address?.address_details?.addressLine1 || "-"}
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-value order-card-cells" style={{ lineHeight: 1.6 }}>
                                                {order?.billing_address?.address_details?.addressLine2 && (
                                                    <span>{order.billing_address.address_details.addressLine2 || "-"}</span>
                                                )}
                                                {(order?.billing_address?.address_details?.city || order?.billing_address?.address_details?.state || order?.billing_address?.address_details?.zip) && (
                                                    <span className={order?.billing_address?.address_details?.addressLine2 ? "d-block" : ""}>
                                                        {[
                                                            order?.billing_address?.address_details?.city,
                                                            order?.billing_address?.address_details?.state,
                                                            order?.billing_address?.address_details?.zip
                                                        ].filter(Boolean).join(", ")}
                                                    </span>
                                                )}
                                                {order?.billing_address?.address_details?.country && (
                                                    <span className="d-block">{order.billing_address.address_details.country}</span>
                                                )}
                                                {!order?.billing_address?.address_details?.addressLine2 &&
                                                    !order?.billing_address?.address_details?.city &&
                                                    !order?.billing_address?.address_details?.state &&
                                                    !order?.billing_address?.address_details?.zip &&
                                                    !order?.billing_address?.address_details?.country && "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Shipping Address */}
                        <div className="col-12 col-lg-6" id="row-overlap">
                            <div className="card box-shadow-datatables shadow-border-2">
                                <div className="px-3 py-3 rounded-top" style={{ backgroundColor: "#eff6ff" }}>
                                    <h6 className="mb-0 d-flex align-items-center order-tittle">
                                        <AppIcon name="shippingAddress" size={22} alt="Shipping Address" />
                                        <p className="mb-0 ms-2">Shipping Address</p>
                                    </h6>
                                </div>

                                <div className="card-body">
                                    <div className="row gy-3">
                                        <div className="col-12 col-lg-6">
                                            <div className="addr-label order-card-title">First Name:</div>
                                            <div className="addr-value order-card-cells">{order?.customer_first_name || "-"}</div>
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <div className="addr-label order-card-title">Last Name:</div>
                                            <div className="addr-value order-card-cells">{order?.customer_last_name || "-"}</div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-label order-card-title">Phone Number:</div>
                                            <div className="addr-value order-card-cells">{order?.shipping_address?.address_details?.phone || "-"}</div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-label order-card-title">Address Line 1:</div>
                                            <div className="addr-value order-card-cells">
                                                {order?.shipping_address?.address_details?.addressLine1 || "-"}
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="addr-value order-card-cells" style={{ lineHeight: 1.6 }}>
                                                {order?.shipping_address?.address_details?.addressLine2 && (
                                                    <span>{order.shipping_address.address_details.addressLine2 || "-"}</span>
                                                )}

                                                {(order?.shipping_address?.address_details?.city || order?.shipping_address?.address_details?.state || order?.shipping_address?.address_details?.zip) && (
                                                    <span className={order?.shipping_address?.address_details?.addressLine2 ? "d-block" : ""}>
                                                        {[
                                                            order?.shipping_address?.address_details?.city,
                                                            order?.shipping_address?.address_details?.state,
                                                            order?.shipping_address?.address_details?.zip
                                                        ].filter(Boolean).join(", ")}
                                                    </span>
                                                )}
                                                {order?.shipping_address?.address_details?.country && (
                                                    <span className="d-block">{order.shipping_address.address_details.country}</span>
                                                )}
                                                {!order?.shipping_address?.address_details?.addressLine2 &&
                                                    !order?.shipping_address?.address_details?.city &&
                                                    !order?.shipping_address?.address_details?.state &&
                                                    !order?.shipping_address?.address_details?.zip &&
                                                    !order?.shipping_address?.address_details?.country && "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* styles to match the screenshot precisely */}
                    <style>{`
  .rounded-4 { border-radius: 16px; }
  .card-header { border-bottom: 1px solid #edf1f5; }
  .addr-label { font-weight: 600; color: #111827; margin-bottom: 4px; }
  .addr-value { color: #111827; }
  .icon-chip {
    width: 28px; height: 28px; border-radius: 8px;
    display:inline-flex; align-items:center; justify-content:center;
    font-size: 16px;
  }
  .chip-amber  { background:#FFF3E6; color:#F59E0B; }   /* billing pin */
  .chip-violet { background:#F3E8FF; color:#7C3AED; }   /* shipping truck */

`}</style>
                    {/* PRODUCT DETAILS */}
                    <div className="card box-shadow-datatables shadow-border-2 ">
                        <div className="py-3 ms-2">
                            <h6 className="mb-0 d-flex align-items-center order-tittle ms-1">
                                <AppIcon name="productBox" size={22} alt="Product Details" />
                                <p className="mb-0 ms-2">Product Details</p>
                            </h6>
                        </div>

                        <div className="card-body pt-3">
                            {/* ===== Desktop table ===== */}
                            <div className="rdt-desktop">
                                <div className="table-responsive sales-order-details-products-scroll">
                                    <table className="table align-middle mb-0 ps-table sales-order-details-products-table">
                                        <colgroup>
                                            <col className="sod-col-image" />
                                            <col className="sod-col-product" />
                                            <col className="sod-col-qty" />
                                            <col className="sod-col-changed" />
                                            <col className="sod-col-discount" />
                                            {/* <col className="sod-col-tax" /> */}
                                            <col className="sod-col-total" />
                                        </colgroup>
                                        <thead>
                                            <tr className="ps-head">
                                                <th className="text-start order-tittle sod-th-image">Image</th>
                                                <th className="text-start order-tittle sod-th-product">Product details</th>
                                                <th className="text-center order-tittle sod-th-qty">Qty</th>
                                                <th className="text-center order-tittle sod-th-changed" title="Unit Price">
                                                    Unit<br />Price
                                                </th>
                                                <th className="text-center order-tittle sod-th-discount">Discount</th>
                                                {/* <th className="text-center order-tittle sod-th-tax">Tax</th> */}
                                                <th className="text-center order-tittle sod-th-total" title="Total Amount">
                                                    Total<br />Amount
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order?.items?.map((item: any, idx: number) => {
                                                const rowKey = `${item.product_id || idx}-${item.variant_id || "v" + idx}`;
                                                const firstFromItem: string | undefined =
                                                    Array.isArray(item?.images) && item.images.length ? item.images[0]?.url : undefined;
                                                const fromMapByImage = firstFromItem ? variantImgMap[firstFromItem] : undefined;
                                                const fromHelperByImage = firstFromItem ? getImageSrc(firstFromItem, authHeaders) : undefined;
                                                const keyByVariant = String(item?.variant_id ?? "");
                                                const keyByProduct = String(item?.product_id ?? "");
                                                const fromMapByVariantOrProduct = variantImgMap[keyByVariant] || variantImgMap[keyByProduct];
                                                const designPreviewSrc = item?.design_id && item?.preview_url
                                                    ? getImageSrc(item.preview_url, variantImgMap1) || variantImgMap1[item.preview_url] || ""
                                                    : "";
                                                const imgSrc = designPreviewSrc || fromHelperByImage || fromMapByImage || fromMapByVariantOrProduct || "";
                                                const productImageSrc = imgSrc || imagecustomRounded;
                                                const hasCustomDesign = Boolean(item?.design_id && item?.preview_url && designPreviewSrc);
                                                const lineId = String(item.sku || item.itemId || item.product_id || "");
                                                const {
                                                    unitRetail: unitPriceDisplay,
                                                    lineDiscount: rowDisc,
                                                    rowTaxAmt,
                                                    lineTotal,
                                                } = getProductLinePricing(item);
                                                return (
                                                    <tr key={rowKey}>
                                                        <td className="sod-image-cell">
                                                            <div className="sod-product-thumb">
                                                                <img
                                                                    src={productImageSrc}
                                                                    alt={item.product_name || item.sku || "Product image"}
                                                                    className="img-fluid rounded"
                                                                    style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                                                    onError={(e) => {
                                                                        (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                                                    }}
                                                                />
                                                            </div>
                                                            {hasCustomDesign && (
                                                                <div className="mt-2" style={{ fontSize: 12 }}>
                                                                    {/* <div className="order-card-log mb-1">Custom Design:</div> */}
                                                                    <button
                                                                        type="button"
                                                                        className="custom-design-action-btn me-2"
                                                                        onClick={() => setCustomDesignPreviewSrc(designPreviewSrc)}
                                                                        title="View Custom Design"
                                                                    >
                                                                        <i className="bi bi-eye me-1 fs-14" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="custom-design-action-btn"
                                                                        onClick={() =>
                                                                            downloadCustomDesign(
                                                                                item.preview_url,
                                                                                item.sku
                                                                                    ? `${String(item.sku).replace(/[^\w.-]+/g, "_")}-custom-design`
                                                                                    : "custom-design"
                                                                            )
                                                                        }
                                                                        title="Download Custom Design"
                                                                    >
                                                                        <i className="bi bi-download me-1 fs-14" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="align-middle sod-product-cell">
                                                            <div className="text-uppercase mb-1 order-card-cells wrap-anywhere">
                                                                {item.product_name}
                                                            </div>
                                                            {/* <p className="order-card-title">{item.sku_}</p> */}
                                                            <p className="order-card-title">{formatSkuDisplay(item.sku)}</p>
                                                            {(() => {
                                                                const fields = Array.isArray(item?.customizations?.fields)
                                                                    ? item.customizations.fields
                                                                    : [];
                                                                if (!fields.length) return null;

                                                                const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";

                                                                const firstFileVal = (raw: any): string => {
                                                                    if (!raw) return "";
                                                                    if (typeof raw === "string") return raw.trim();
                                                                    if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
                                                                    return "";
                                                                };

                                                                const hasValue = (f: any): boolean => {
                                                                    if (isFile(f)) {
                                                                        const idOrUrl = firstFileVal(f?.value);
                                                                        return !!idOrUrl; // show section if any file has an id/URL
                                                                    }
                                                                    // text-like
                                                                    const v = (f?.value ?? "").toString().trim();
                                                                    return v.length > 0;
                                                                };

                                                                // ✅ Only show the whole block if at least one field has a value
                                                                const anyHasValue = fields.some(hasValue);
                                                                if (!anyHasValue) return null;

                                                                // Order: non-file fields first, file fields last (only rows with a real value)
                                                                const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
                                                                const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));

                                                                const renderText = (field: any, idx: number) => {
                                                                    const key = `${rowKey}-cf-txt-${field?._id || field?.field_name || idx}`;
                                                                    const valStr = String(field?.value ?? "").trim();

                                                                    return (
                                                                        <div key={key} className="mb-2" style={{ maxWidth: 400, wordBreak: "break-word" }}>
                                                                            <strong className="custom-cells order-card-log">
                                                                                {field?.field_name || "Field"} :
                                                                            </strong>{" "}
                                                                            <span className="order-card-log wrap-anywhere">{valStr}</span>
                                                                        </div>
                                                                    );
                                                                };

                                                                const renderFile = (field: any, idx: number) => {
                                                                    const key = `${rowKey}-cf-file-${field?._id || field?.field_name || idx}`;
                                                                    const first = firstFileVal(field?.value);

                                                                    // Resolve via your map-based helper; allow absolute URLs as fallback
                                                                    const fromHelper = first ? getImageSrc(first, variantImgMap1) : "";
                                                                    const fromAbsolute = first && /^(https?:|data:|blob:)/i.test(first) ? first : "";
                                                                    const imgSrc = fromHelper || fromAbsolute || "";

                                                                    return (
                                                                        <div key={key} className="d-flex flex-column gap-1 mb-2" style={{ maxWidth: 400, wordBreak: "break-word" }}>
                                                                            <strong className="custom-cells order-card-log">
                                                                                {field?.field_name || "Image"} :
                                                                            </strong>{" "}
                                                                            {imgSrc ? (
                                                                                <a href={imgSrc} download target="_blank" rel="noopener noreferrer">
                                                                                    <img
                                                                                        src={imgSrc}
                                                                                        alt={field?.field_name || "Customization image"}
                                                                                        className="img-fluid rounded order-card-log"
                                                                                        style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                                                    />
                                                                                </a>
                                                                            ) : first && /^[0-9a-f]{24}$/i.test(first) ? (
                                                                                <span style={{ color: "#999" }}>Loading…</span>
                                                                            ) : (
                                                                                <span style={{ color: "#999", fontStyle: "italic" }}>--</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                };

                                                                return (
                                                                    <div className="custom-fields-container mt-2">
                                                                        {/* Text fields first */}
                                                                        {textFields.map(renderText)}
                                                                        {/* File/image fields last */}
                                                                        {fileFields.map(renderFile)}
                                                                    </div>
                                                                );
                                                            })()}

                                                            {renderLineItemCustomInformation(rowKey, item)}

                                                            {/* {(() => {
                                                                const fields = Array.isArray(item?.customizations?.fields)
                                                                    ? item.customizations.fields
                                                                    : [];
                                                                if (!fields.length) return null;

                                                                return (
                                                                    <div className="custom-fields-container mt-2">
                                                                        {fields.map((field: any, idx: number) => {
                                                                            const key = `${rowKey}-cf-${field?._id || field?.field_name || idx}`;
                                                                            const isFile = String(field?.field_type || "").toLowerCase() === "file";

                                                                            if (isFile) {
                                                                                const raw = field?.value;
                                                                                const first =
                                                                                    typeof raw === "string"
                                                                                        ? raw.trim()
                                                                                        : (raw?.url ? String(raw.url).trim() : "");

                                                                                // use the SAME helper as product images
                                                                                const fromHelper = getImageSrc(first, variantImgMap1);

                                                                                // allow absolute url as a fallback (rare)
                                                                                const fromAbsolute =
                                                                                    first && /^(https?:|data:|blob:)/i.test(first) ? first : "";

                                                                                const imgSrc = fromHelper || fromAbsolute || "";

                                                                                return (
                                                                                    <div key={key} className="mb-2" style={{ maxWidth: 400, wordBreak: "break-word" }}>
                                                                                        <strong className="custom-cells order-card-log">
                                                                                            {field?.field_name || "Image"} :
                                                                                        </strong>{" "}
                                                                                        {imgSrc ? (
                                                                                            <a href={imgSrc} download target="_blank" rel="noopener noreferrer">
                                                                                                <img
                                                                                                    src={imgSrc}
                                                                                                    alt={field?.field_name || "Customization image"}
                                                                                                    className="img-fluid rounded border order-card-log"
                                                                                                    style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                                                                />
                                                                                            </a>
                                                                                        ) : /^[0-9a-f]{24}$/i.test(first || "") ? (
                                                                                            <span style={{ color: "#999" }}>Loading…</span>
                                                                                        ) : (
                                                                                            <span style={{ color: "#999", fontStyle: "italic" }}>--</span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            }

                                                                            // non-file
                                                                            const valStr = String(field?.value ?? "").trim();
                                                                            return (
                                                                                <div key={key} className="mb-2" style={{ maxWidth: 400, wordBreak: "break-word" }}>
                                                                                    <strong className="custom-cells order-card-log">{field?.field_name || "Field"} :</strong>{" "}
                                                                                    <span className="order-card-log wrap-anywhere">
                                                                                        {valStr || <span style={{ color: "#999", fontStyle: "italic" }}>--</span>}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                );
                                                            })()} */}
                                                        </td>

                                                        {/* <td className="align-middle">
                                                            <div className=" text-uppercase mb-1 order-card-cells wrap-anywhere">{item.product_name}</div>
                                                            <p className="order-card-title">{item.sku}</p>


                                                            {visibleFields.length > 0 && (
                                                                <div className="custom-fields-container mt-2">
                                                                    {visibleFields.map((field: any, fidx: number) => {
                                                                        const isFile = field?.field_type === "file";
                                                                        const val = String(field?.value ?? "").trim();
                                                                        const key = `${rowKey}-${field?._id || field?.field_name || fidx}`;

                                                                        return (
                                                                            <div key={key} className="mb-2" style={{ wordBreak: "break-word", maxWidth: 400 }}>
                                                                                {isFile ? (
                                                                                    val ? (
                                                                                        <>
                                                                                            <div className="mb-1 order-card-log">{field.field_name}</div>
                                                                                            <a
                                                                                                href={`${baseUrl}/${val.replace(/\\/g, "/")}`}
                                                                                                download
                                                                                                target="_blank"
                                                                                                rel="noopener noreferrer"
                                                                                            >
                                                                                                <img
                                                                                                    src={`${baseUrl}/${val.replace(/\\/g, "/")}`}
                                                                                                    alt={field.field_name}
                                                                                                    className="rounded order-card-log"
                                                                                                    style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                                                                />
                                                                                            </a>
                                                                                        </>
                                                                                    ) : (
                                                                                        <div>
                                                                                            <strong className="custom-cells order-card-log">{field.field_name} :</strong>{" "}
                                                                                            <span style={{ color: "#999", fontStyle: "italic" }}>--</span>
                                                                                        </div>
                                                                                    )
                                                                                ) : (
                                                                                    <div>
                                                                                        <strong className="custom-cells order-card-log">{field.field_name} :</strong>{" "}
                                                                                        <span className="order-card-log wrap-anywhere">
                                                                                            {val || <span style={{ color: "#999", fontStyle: "italic" }}>---</span>}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </td> */}

                                                        <td className="text-center order-card-cells sod-qty-cell">{item.quantity}</td>
                                                        <td className="text-center order-card-cells sod-changed-cell">{unitPriceDisplay.toFixed(2)}</td>
                                                        <td className="text-center order-card-cells sod-discount-cell">
                                                            <DiscountAmountDisplay amount={rowDisc} />
                                                        </td>

                                                        {/* <td className="text-center order-card-cells">
                                                            {tinfo && (tinfo.amount > 0 || tinfo.rate > 0)
                                                                ? (<>
                                                                    ${Number(tinfo.amount || 0).toFixed(2)}
                                                                    <div className="text-muted small">{Number((tinfo.rate || 0) * 100).toFixed(2)}%</div>
                                                                </>)
                                                                : <span>--</span>
                                                            }
                                                        </td> */}
                                                        {/* <td className="text-center order-card-cells sod-tax-cell">
                                                            {rowTaxAmt > 0 ? rowTaxAmt.toFixed(2) : "-"}
                                                        </td> */}
                                                        <td className="text-center order-card-cells sod-total-cell">
                                                            <span className="sod-total-value">${lineTotal.toFixed(2)}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ===== Mobile cards (<=1021px) — compact tile ===== */}
                            <div className="rdt-mobile">
                                {order?.items?.length ? (
                                    order.items.map((item: any, idx: number) => {
                                        const rowKey = `${item.product_id || idx}-${item.variant_id || "v" + idx}`;
                                        const firstFromItem: string | undefined =
                                            Array.isArray(item?.images) && item.images.length ? item.images[0]?.url : undefined;
                                        const fromMapByImage = firstFromItem ? variantImgMap[firstFromItem] : undefined;
                                        const fromHelperByImage = firstFromItem ? getImageSrc(firstFromItem, authHeaders) : undefined;
                                        const keyByVariant = String(item?.variant_id ?? "");
                                        const keyByProduct = String(item?.product_id ?? "");
                                        const fromMapByVariantOrProduct = variantImgMap[keyByVariant] || variantImgMap[keyByProduct];
                                        const designPreviewSrc = item?.design_id && item?.preview_url
                                            ? getImageSrc(item.preview_url, variantImgMap1) || variantImgMap1[item.preview_url] || ""
                                            : "";
                                        const hasCustomDesign = Boolean(item?.design_id && item?.preview_url && designPreviewSrc);
                                        const imgUrl =
                                            designPreviewSrc ||
                                            fromHelperByImage ||
                                            fromMapByImage ||
                                            fromMapByVariantOrProduct ||
                                            (item.image_url ? `${baseUrl}${item.image_url}` : undefined);
                                        const productImageSrc = imgUrl || imagecustomRounded;

                                        // best-effort meta lines like your screenshot
                                        const brand = item.brand || item.brand_name || item.vendor || "";
                                        const category =
                                            item.category ||
                                            item.category_name ||
                                            item.product_type ||
                                            item.collection ||
                                            "";

                                        const fmtMoney = (n: any) =>
                                            Number.isFinite(Number(n))
                                                ? Number(n).toLocaleString(undefined, { style: "currency", currency: "USD" })
                                                : String(n ?? "—");

                                        const {
                                            unitRetail: unitPriceDisplay,
                                            lineDiscount: rowDisc,
                                            rowTaxAmt,
                                            lineTotal,
                                        } = getProductLinePricing(item);

                                        return (
                                            <div key={rowKey} className="card border-0 shadow-sm mb-3">
                                                <div className="card-body py-3">
                                                    <div className="d-flex gap-3 align-items-start">
                                                        {/* image */}
                                                        <div className="psm-img">
                                                            <img
                                                                src={productImageSrc}
                                                                alt={item?.product_name || "Product image"}
                                                                className="img-fluid rounded "
                                                                style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                                                onError={(e) => {
                                                                    (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                                                }}
                                                            />
                                                            {hasCustomDesign && (
                                                                <div className="mt-2" style={{ fontSize: 12 }}>
                                                                    <span className="order-card-log me-1">Custom Design:</span>
                                                                    <button
                                                                        type="button"
                                                                        className="custom-design-action-btn me-2"
                                                                        onClick={() => setCustomDesignPreviewSrc(designPreviewSrc)}
                                                                        title="View Custom Design"
                                                                    >
                                                                        <i className="bi bi-eye me-1" />
                                                                        View
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="custom-design-action-btn"
                                                                        onClick={() =>
                                                                            downloadCustomDesign(
                                                                                item.preview_url,
                                                                                item.sku
                                                                                    ? `${String(item.sku).replace(/[^\w.-]+/g, "_")}-custom-design`
                                                                                    : "custom-design"
                                                                            )
                                                                        }
                                                                        title="Download Custom Design"
                                                                    >
                                                                        <i className="bi bi-download me-1" />
                                                                        Download
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* text */}
                                                        <div className="flex-grow-1">
                                                            <div className="psm-title text-uppercase wrap-anywhere">
                                                                {item.product_name}
                                                            </div>
                                                            {renderLineItemCustomInformation(rowKey, item)}
                                                            {brand && <div className="psm-sub">{brand}</div>}
                                                            {category && <div className="psm-sub">{category}</div>}
                                                        </div>
                                                    </div>

                                                    <div className="row mt-3 g-2 text-center">
                                                        <div className="col-4">
                                                            <div className="psm-label">Quantity</div>
                                                            <div className="psm-value">{item.quantity}</div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="psm-label">Unit Price</div>
                                                            <div className="psm-value">{fmtMoney(unitPriceDisplay)}</div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="psm-label">Discount</div>
                                                            <div className="psm-value">
                                                                <DiscountAmountDisplay amount={rowDisc} />
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="psm-label">Tax</div>
                                                            <div className="psm-value">
                                                                {rowTaxAmt > 0 ? fmtMoney(rowTaxAmt) : "-"}
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="psm-label">Total</div>
                                                            <div className="psm-value">{fmtMoney(lineTotal)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-muted py-3">No items.</div>
                                )}
                            </div>

                        </div>

                        {/* responsive toggle */}
                        <style>{`
    .sales-order-details-products-scroll {
      width: 100%;
      max-width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    .sales-order-details-products-table {
      table-layout: fixed;
      width: 100%;
      min-width: 0;
    }
    .sales-order-details-products-table .sod-col-image { width: 8%; }
    .sales-order-details-products-table .sod-col-product { width: 22%; }
    .sales-order-details-products-table .sod-col-qty { width: 7%; }
    .sales-order-details-products-table .sod-col-changed { width: 12%; }
    .sales-order-details-products-table .sod-col-discount { width: 11%; }
    .sales-order-details-products-table .sod-col-tax { width: 9%; }
    .sales-order-details-products-table .sod-col-total { width: 13%; }
    .sales-order-details-products-table th.order-tittle {
      font-size: 0.78rem;
      white-space: normal;
      line-height: 1.15;
      vertical-align: middle;
      padding: 0.5rem 0.35rem;
    }
    .sales-order-details-products-table td.order-card-cells {
      padding: 0.5rem 0.35rem;
      font-size: 0.875rem;
      white-space: nowrap;
    }
    .sales-order-details-products-table .sod-product-cell {
      word-break: break-word;
      white-space: normal;
    }
    .sales-order-details-products-table .sod-product-thumb {
      width: 56px;
      height: 56px;
      flex-shrink: 0;
    }
    .sales-order-details-products-table .sod-total-value {
      display: inline-block;
      white-space: nowrap;
    }
    @media (max-width: 1400px) {
      .sales-order-details-products-table {
        min-width: 900px;
      }
    }
    .rdt-desktop { display: block; }
    .rdt-mobile  { display: none; }
    @media (max-width: 1021px){
      .rdt-desktop { display: none; }
      .rdt-mobile  { display: block; }
    }
      /* compact mobile tile look */
.psm-img{ width:72px; height:72px; flex-shrink:0; }
.psm-title{ font-weight:700; font-size:.95rem; line-height:1.15; }
.psm-sub{ font-size:.78rem; color:#6b7280; line-height:1.05; }
.psm-label{ font-size:.77rem; color:#6b7280; }
.psm-value{ font-weight:700; font-size:.9rem; }
.custom-design-action-btn{
  border: 1px solid #d1d5db;
  background: #f8fafc;
  color: #334155;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 1.4;
}
.custom-design-action-btn:hover{
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #1e3a8a;
}

  `}</style>
                    </div>
                    {linkedProjectId && hasApprovedMessage ? (
                        <>
                            {approvedProofCards.map((card) => (
                                <CompactApprovedProofCard
                                    key={`approved-proof-v-${card.version}`}
                                    version={card.version}
                                    files={card.files}
                                    approvedBy={card.approvedByName}
                                    approvedAt={card.approvedAtLabel}
                                    footerBy={card.footerBy}
                                    shippingName={card.shippingName}
                                    shippingDesc={card.shippingDesc}
                                    shippingAmountLabel={card.shippingAmountLabel}
                                    showOrderSummary={Boolean(card.orderSummary)}
                                    orderSummary={card.orderSummary}
                                    onDownloadByFileId={(fileId, fileName) => {
                                        void downloadWorkDriveFile(fileId, fileName);
                                    }}
                                />
                            ))}
                        </>
                    ) : null}

                    {customDesignPreviewSrc && (
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                background: "rgba(0,0,0,0.65)",
                                zIndex: 9999,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 16,
                            }}
                            onClick={() => setCustomDesignPreviewSrc(null)}
                        >
                            <div
                                style={{
                                    background: "#fff",
                                    borderRadius: 10,
                                    padding: 12,
                                    maxWidth: "90vw",
                                    maxHeight: "90vh",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="d-flex justify-content-end mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => setCustomDesignPreviewSrc(null)}
                                    >
                                        <i className="bi bi-x-lg" />
                                    </button>
                                </div>
                                <img
                                    src={customDesignPreviewSrc}
                                    alt="Custom Design Preview"
                                    style={{ maxWidth: "86vw", maxHeight: "78vh", objectFit: "contain" }}
                                />
                            </div>
                        </div>
                    )}

                    {/* ORDER NOTES + ORDER TOTALS */}
                    <div className="row g-4 ms-1" id="row-overlap">
                        {/* Order Notes */}
                        <div className="col-12 col-lg-6" id="row-overlap">
                            <Instructions
                                title="Instructions"
                                mode="instructions_files"
                                loginType={login == "signage" ? "signage" : "agent"}
                                instructions={notes}
                                attachments={instructionAttachments}
                                orderDocumentId={String(order?._id || order?.id || "")}
                                collectionName="sales"
                                placeholder="Add instructions here"
                                rows={4}
                                onSave={handleSaveInstructionsOnly}
                                onAfterFilesUploaded={onRefreshOrder}
                                className="mt-3"
                                iconColor="#10B981"
                                iconBackground="#EAF7F1"
                                headerBgColor="#eff6ff"
                            />
                            {/* <div className="card shadow-sm rounded-4 border-0">
                                <div className=" py-3 mt-3">
                                    <h6 className="mb-0 d-flex align-items-center order-tittle">
                                        <span
                                            aria-hidden="true"
                                            className="image-icons"
                                            style={{
                                                backgroundColor: "#9810FA",
                                                WebkitMaskImage: `url(${BillingAddress})`,
                                                maskImage: `url(${BillingAddress})`,
                                            }}
                                        />
                                        Order Notes
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <textarea
                                        className="form-control notes-input"
                                        rows={6}
                                        placeholder="Add any notes related to this order..."
                                    />
                                </div>
                            </div> */}
                        </div>

                        {/* Order Totals */}
                        <div className="col-12 col-lg-6" id="row-overlap">
                            <div className="card box-shadow-datatables shadow-border-2">
                                <div className="px-3 py-3 rounded-top d-flex align-items-center" style={{ backgroundColor: "#eff6ff" }}>
                                    <h6 className="mb-0 d-flex align-items-center order-tittle">
                                        <AppIcon name="shippingTruck" size={22} alt="Order Totals" />
                                        <span className="ms-2">Order Totals</span>
                                    </h6>
                                </div>
                                <div className="card-body">
                                    {/* Subtotal */}
                                    {/* <div className="d-flex justify-content-between py-1">
                                        <span className="order-card-title">Sub Total:</span>
                                        <span className="order-card-cells">{`$${Number(subtotalComputed || 0).toFixed(2)}`}</span>
                                    </div>
                                    <div className="d-flex justify-content-between py-1">
                                        <span className="order-card-title">Shipping Charge:</span>
                                        <span className="order-card-cells">{`$${Number(shippingAmountComputed || 0).toFixed(2)}`}</span>
                                    </div>
                                    <div className="d-flex justify-content-between py-1">
                                        <span className="order-card-title">Special Fee:</span>
                                        <span className="order-card-cells">{`$${Number(specialFeeAmountComputed || 0).toFixed(2)}`}</span>
                                    </div> */}
                                    <div className="d-flex justify-content-between py-1">
                                        <span className="order-card-title">Sub Total:</span>
                                        <span className="order-card-cells">
                                            ${subTotalToShow.toFixed(2)}
                                        </span>
                                    </div>

                                    {(discountToShow > 0 || discountCodeToShow) && (
                                        <div className="d-flex justify-content-between py-1 text-success">
                                            <span className="order-card-title">
                                                Discount:
                                                {discountCodeToShow ? (
                                                    <span style={{ color: "#00a63e" }}> ({discountCodeToShow})</span>
                                                ) : null}
                                            </span>
                                            <span className="order-card-cells" style={{ color: "#00a63e" }}>
                                                {discountToShow > 0 ? `-$${discountToShow.toFixed(2)}` : "$0.00"}
                                            </span>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                                        <span className="order-card-title text-break" style={{ maxWidth: "62%" }}>
                                            {shippingChargeTotalsLabel}
                                        </span>
                                        <span className="order-card-cells flex-shrink-0">
                                            {n(shippingAmountComputed) > 0 ? `$${n(shippingAmountComputed).toFixed(2)}` : "$0.00"}
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between py-1 align-items-start gap-2">
                                        <span
                                            className="order-card-title text-break"
                                            style={{ maxWidth: "62%" }}
                                            title={specialFeeBreakdownDisplay.tooltip}
                                        >
                                            {specialFeeBreakdownDisplay.labelText}:
                                        </span>
                                        <span className="order-card-cells flex-shrink-0">
                                            {n(specialFeeAmountComputed) > 0 ? `$${n(specialFeeAmountComputed).toFixed(2)}` : "$0.00"}
                                        </span>
                                    </div>


                                    {/* Tax with arrow & breakdown */}
                                    <div className="py-1">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <button
                                                type="button"
                                                onClick={() => setShowTaxDetails(v => !v)}
                                                className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                                                aria-expanded={showTaxDetails}
                                                aria-controls="tax-breakdown"
                                            >
                                                {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                                                <span className="order-card-title">Tax :</span>
                                            </button>
                                            <span className="order-card-cells">
                                                {`$${Number(taxTotalComputed || 0).toFixed(2)}`}
                                            </span>
                                        </div>

                                        {/* {showTaxDetails && (
                                            <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span>Products Tax</span>
                                                    <span>${Number(taxBreakdown.product || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span>Shipping Tax</span>
                                                    <span>${Number(taxBreakdown.shipping || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                    <span>Special Fee Tax</span>
                                                    <span>${Number(taxBreakdown.specialFee || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        )} */}
                                    </div>

                                    <hr className="my-2" />
                                    <div className="d-flex justify-content-between pt-1">
                                        <strong className="order-card-title fs-19">Total (USD):</strong>
                                        <span className="order-card-cells fw-semibold">
                                            ${grandTotalPanel.toFixed(2)}
                                        </span>
                                    </div>
                                    <hr className="my-2" />
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="fw-medium">Already Paid:</span>
                                        <span className="fw-semibold">${totalPaid.toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center py-2">
                                        <span className="fw-medium">Remaining Balance:</span>
                                        <span className="fw-semibold">
                                            {order?.payment_status === "Overpaid"
                                                ? `+$${Math.abs(remainingBalance).toFixed(2)}`
                                                : `$${remainingBalance.toFixed(2)}`}
                                        </span>
                                    </div>

                                    {/* Grand Total */}
                                    {/* <div className="d-flex justify-content-between pt-1">
                                        <strong className="order-card-title fs-19">Total (USD):</strong>
                                        <strong className="order-card-title fs-19">{`$${Number(grandTotalComputed || 0).toFixed(2)}`}</strong>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                        {/* Action bar */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            {/* LEFT: resolve tax */}
                            <div className="d-flex align-items-center">
                                {/* {order?.status !== 400 &&
                                    login === "signage" &&
                                    normalizedTaxStatus !== "complete" &&
                                    activeTaxType === "taxcloud" &&
                                    !salesResolveTaxCommitted ? (
                                        <div className="d-flex gap-3 mt-3 mb-3 p-0">
                                            <button
                                                type="button"
                                                className="btn export-button"
                                                disabled={salesResolveTaxLoading}
                                                onClick={() => void applySalesOrderResolveTax("draft")}
                                            >
                                                {salesResolveTaxLoading ? "Fetching…" : "Get Live Taxes"}
                                            </button>
                                        </div>
                                    ) : null} */}
                            </div>
                            {isSignage ? (
                                <ul className="list-group list-group-flush">
                                    {/* Shipped — paid & status 101 */}
                                    {canShowShipped && (
                                        <li className="list-group-item px-0">
                                            <button
                                                className=" export-button w-100"
                                                onClick={() =>
                                                    onOrderStatus(
                                                        login === "signage" && hasCustomerPickupShipping ? 300 : 301
                                                    )
                                                }
                                            >
                                                <i className={`bi ${hasCustomerPickupShipping ? "bi-bag-check" : "bi-truck"} me-2`}></i>
                                                {hasCustomerPickupShipping ? "Ready To Pickup" : "Shipped"}
                                            </button>
                                        </li>
                                    )}

                                    {/* {order?.status === 100 && (
                                        <li className="list-group-item px-0">
                                            <button
                                                className="btn btn-info w-100 br-8"
                                                onClick={() => onOrderStatus(101)}
                                            >
                                                <i className="bi bi-check2-circle me-2"></i>Confirm
                                            </button>
                                        </li>
                                    )} */}

                                    {(order?.status === 300 || order?.status === 301) && (
                                        <li className="list-group-item px-0">
                                            <button
                                                className="export-button w-100"
                                                onClick={handleMarkAsDelivered}
                                            >
                                                <i className="bi bi-bag-check me-2"></i>Mark as Delivered
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            ) : (
                                <div className="text-muted">No actions available.</div>
                            )}
                        </div>
                        <div className="d-flex flex-column align-items-start gap-3 p-0">

                            {(orderIdForShipments || orderNumberForShipments) ? (
                                <SalesOrderDetailsShipmentsTable
                                    token={tokenStr}
                                    orderIdFilter={orderIdForShipments}
                                    orderNumberFilter={orderNumberForShipments}
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* styles to match screenshot precisely */}
                    <style>{`
  .rounded-4 { border-radius: 16px; }
  .card-header { border-bottom: 1px solid #edf1f5; }
  .icon-chip {
    width: 28px; height: 28px; border-radius: 8px;
    display:inline-flex; align-items:center; justify-content:center;
    font-size: 16px;
  }
  .chip-teal { background:#EAF7F1; color:#10B981; }
  .notes-input {
    background:#f3f6fb; border-color:#e9eef5; border-radius:12px;
  }
  .notes-input:focus {
    background:#f3f6fb; box-shadow:none; border-color:#d9e3ef;
  }
`}</style>
                </div>

                {/* Right column */}
                {/* {order && order.status > 100 && ( */}
                <div className="col-lg-4 mt-lg-4" id="right-column">
                    <PONumber
                        title="PO Number"
                        value={String((order as any)?.po_number ?? (order as any)?.poNumber ?? "")}
                        placeholder="e.g. PO-2026-00124"
                        className="mb-3"
                        orderId={String(order?._id || order?.id || "")}
                        orderType="sales"
                        onSaved={async () => {
                            await onRefreshOrder?.();
                        }}
                    />

                    {/* SHIPPING DETAILS */}
                    {/* Shipping Details Card */}
                    {/* <div className="card box-shadow-datatables shadow-border-2"> */}
                    <OrderChargesView
                        className="mt-3"
                        shipping_option={shippingRows}
                        specialfee={specialFeeRows}
                    />
                    {/* </div> */}
                    {gatedRightTopPanel}
                    {/* PAYMENT DETAILS */}
                    <div className="card box-shadow-datatables shadow-border-2">
                        <div className="px-3 py-3 rounded-top" style={{ backgroundColor: "#eff6ff" }}>
                            <h6 className="mb-0 d-flex align-items-center order-tittle ">
                                <AppIcon name="paymentDetails" size={22} alt="Payment Details" />
                                <p className="mb-0 ms-2">Payment Details</p>
                            </h6>
                        </div>
                        <div className="txn-scroll rounded-3 border">
                            {Array.isArray(order?.payment_info) && order.payment_info.length > 0 ? (
                                order.payment_info.map((payment: any, index: number) => {
                                    const key = payment?._id || payment?.payment_id || index;
                                    const formattedDate = payment?.payment_date
                                        ? new Date(payment.payment_date).toLocaleDateString("en-GB")
                                        : "—";

                                    const fmtMoney = (v: any) =>
                                        Number.isFinite(Number(v))
                                            ? Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" })
                                            : "—";
                                    const hasAmountPaid = payment?.amount_paid !== undefined && payment?.amount_paid !== null && payment?.amount_paid !== "";
                                    const hasAmountDue = payment?.amount_due !== undefined && payment?.amount_due !== null && payment?.amount_due !== "";
                                    {/* {order?.payment_info?.length > 0 ? (
                                order?.payment_info?.map((payment: any, index: any) => {
                                    const formattedDate = payment.payment_date
                                        ? new Date(payment.payment_date).toLocaleDateString("en-GB")
                                        : ""; */}
                                    return (
                                        <div key={key} className="fs-14 px-3  py-3">
                                            <div className="mb-3">
                                                <div className="order-card-title">Transaction ID:</div>
                                                <div className="order-card-cells">{payment.payment_id || ""}</div>
                                            </div>
                                            <div className="mb-3">
                                                <div className="order-card-title">Payment Method:</div>
                                                <div className="order-card-cells">{payment.method}</div>
                                            </div>
                                            <div className="mb-3">
                                                <div className="order-card-title">Payment Date:</div>
                                                <div className="order-card-cells">{formattedDate}</div>
                                            </div>
                                            <div className="mb-3">
                                                <div className="order-card-title">Paid By:</div>
                                                <div className="order-card-cells">{displayPaymentPaidByName(payment)}</div>
                                            </div>
                                            <div className="mb-3">
                                                <div className="order-card-title">Total Amount:</div>
                                                <div className="order-card-cells">${payment.total_amount}</div>
                                            </div>
                                            {
                                                hasAmountPaid &&
                                                <div className="mb-3">
                                                    <div className="order-card-title">Amount Paid:</div>
                                                    <div className="order-card-cells">${payment?.amount_paid}</div>
                                                </div>
                                            }
                                            {
                                                hasAmountDue &&
                                                <div className="mb-3">
                                                    <div className="order-card-title">Amount Due:</div>
                                                    <div className="order-card-cells">${payment?.amount_due}</div>
                                                </div>
                                            }
                                            {index !== order.payment_info.length - 1 && <hr className="txn-sep my-2" />}
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-muted text-center">No transactions found</p>
                            )}
                        </div>
                    </div>

                    <style>{SALES_ORDER_DETAILS_PAYMENT_TXN_CSS}</style>
                </div>
                {/* )} */}
            </div>

            <SalesOrderDetailsOrderLog
                mergedOrderLogItems={mergedOrderLogItems}
                colors={colors}
                parseDate={parseDate}
                order={order}
            />

        </div>
    );
}
